import { createContext, useEffect, useState, useCallback, useRef } from "react";
import { getAllData } from "../services/api";
import { addCustomer, updateCustomer, deleteCustomer } from "../services/customer";
import { addSchemeCard, updateSchemeCard, deleteSchemeCard } from "../services/schemecard";
import { addCustomerTransaction, updateCustomerTransaction, deleteCustomerTransaction, addCustomerTransactionAndUpdateStock as apiAddCustomerTransactionAndUpdateStock, removeCustomerTransactionAndUpdateStockApi } from "../services/customertransaction";
import { addStock, updateStock, deleteStock } from "../services/stock";
import { addSupplier, updateSupplier, deleteSupplier } from "../services/supplier";
import { addGirvi, updateGirvi, deleteGirvi } from "../services/girvi";
import { addGirviPayment, updateGirviPayment, deleteGirviPayment } from "../services/girviPayment";
import { bulkUpdateStock as apiBulkUpdateStock, bulkDeleteStock as apiBulkDeleteStock } from "../services/bulkStock";
import { addSupplierTransaction, updateSupplierTransaction, deleteSupplierTransaction } from "../services/supplierTransaction";
import { closeRoznamaDay, addRoznamaDay, updateRoznamaDay, deleteRoznamaDay } from "../services/roznama";
import {
  updateCompanyDetails,
  addCompanyDetails as apiAddCompanyDetails,
  login as apiLogin,
  sendPasswordResetOtp,
  verifyPasswordResetOtp,
  resetPassword,
  updatePassword as apiUpdatePassword,
} from "../services/auth";
import {
  getSilverStock as apiGetSilverStock,
  getSilverTransactions as apiGetSilverTransactions,
  updateSilverStock as apiUpdateSilverStock,
  bulkUpdateSilverStock as apiBulkUpdateSilverStock,
  initializeSilverStock as apiInitializeSilverStock
} from "../services/silvermanagement";
import { sessionManager } from "./sessionManager";
import {
  addAdvancePayment as apiAddAdvancePayment,
  getCustomerAdvancePayments,
  advanceUseForBill,
  getAllAdvancePayments,
  getAdvancePaymentSummary
} from "../services/advancepayment";

export const DataContext = createContext();

const getTodayISTDate = () => {
  const now = new Date();
  // Convert to IST (UTC+5:30)
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));

  // Get date in YYYY-MM-DD format
  const year = istTime.getUTCFullYear();
  const month = String(istTime.getUTCMonth() + 1).padStart(2, '0');
  const day = String(istTime.getUTCDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const DataProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    Customers: [],
    CustomerTransactions: [],
    Stock: [],
    Suppliers: [],
    SupplierTransactions: [],
    Girvi: [],
    GirviPayments: [],
    Roznama: [],
    CompanyDetails: [],
    SilverStock: {
      '999silver': 0,
      '925silver': 0,
      'mixsilver': 0,
      'silver': 0,
      'oldsilver': 0,
      'gold': 0,
      timestamp: new Date().toISOString()
    },
    SilverTransactions: [],
    SchemeCard: []
  });
  const [currentUser, setCurrentUser] = useState(() => sessionManager.getSession());
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!sessionManager.getSession());
  const initialLoadRef = useRef(false);

  // useEffect(() => {
  //   loadInitialData();
  //   console.log("DataContext initialized");
  // }, []);

  // Helper function to format sold description for display
  const formatSoldDescription = useCallback((soldDesc) => {
    if (!soldDesc) return "Not sold";

    try {
      const desc = typeof soldDesc === 'string' ? JSON.parse(soldDesc) : soldDesc;
      return `Sold on: ${desc.sold_date || 'N/A'}, Bill No: ${desc.sold_bill_no || 'N/A'}`;
    } catch (e) {
      return "Sold (details unavailable)";
    }
  }, []);

  // Function to mark stock as sold with description
  const markStockAsSold = useCallback(async (stockId, soldDetails) => {
    setLoading(true);
    try {
      // Get the stock item
      const stockItem = data.Stock.find(item => item.id === stockId);
      if (!stockItem) {
        throw new Error("Stock item not found");
      }

      const soldDescription = {
        sold_date: soldDetails.date || new Date().toISOString().split('T')[0],
        sold_bill_no: soldDetails.billNo || "N/A",
        customer_id: soldDetails.customerId,
        transaction_id: soldDetails.transactionId,
        customer_name: soldDetails.customerName,
        ...soldDetails.additionalDetails
      };

      const updatedData = {
        ...stockItem,
        Sold: "Yes",
        SoldDescription: JSON.stringify(soldDescription)
      };

      // Update in context
      setData(prev => ({
        ...prev,
        Stock: prev.Stock.map(item =>
          item.id === stockId ? { ...item, ...updatedData } : item
        )
      }));

      // Also call API to update on server
      const response = await updateStock(stockId, updatedData);

      if (response?.success) {
        return {
          success: true,
          message: "Stock marked as sold successfully"
        };
      } else {
        // Revert the change if API fails
        setData(prev => ({
          ...prev,
          Stock: prev.Stock.map(item =>
            item.id === stockId ? stockItem : item
          )
        }));
        throw new Error(response?.error || "Failed to update stock on server");
      }

    } catch (error) {
      console.error("Error marking stock as sold:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to mark stock as sold"
      };
    } finally {
      setLoading(false);
    }
  }, [data.Stock]);

  // Function to get sold items
  const getSoldItems = useCallback(() => {
    return data.Stock.filter(item => item.Sold === "Yes");
  }, [data.Stock]);

  // Function to get sold description for a specific item
  const getSoldDescription = useCallback((stockId) => {
    const item = data.Stock.find(item => item.id === stockId);
    if (!item || item.Sold !== "Yes") return null;

    try {
      return typeof item.SoldDescription === 'string'
        ? JSON.parse(item.SoldDescription)
        : item.SoldDescription;
    } catch (e) {
      console.error("Error parsing sold description:", e);
      return null;
    }
  }, [data.Stock]);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getAllData();
      console.log(response);
      const fetchedData = response?.data || {};
      const hasSilverData = fetchedData?.SilverStock &&
        (fetchedData.SilverStock['999silver'] > 0 ||
          fetchedData.SilverStock['925silver'] > 0 ||
          (fetchedData.SilverTransaction && fetchedData.SilverTransaction.length > 0));
      console.log("Loaded data from API:", fetchedData);
      console.log("SilverStock in response:", fetchedData.SilverStock);
      console.log("SilverTransaction in response:", fetchedData.SilverTransaction);

      // Initialize with fallback values if silver data is missing
      const defaultSilverStock = {
        '999silver': 0,
        '925silver': 0,
        'mixsilver': 0,
        'silver': 0,
        'oldsilver': 0,
        'gold': 0,
        timestamp: new Date().toISOString()
      };

      // Helper function to parse SoldDescription
      const parseSoldDescriptionFromBackend = (soldDesc) => {
        if (!soldDesc) return null;

        if (typeof soldDesc === 'string') {
          try {
            return JSON.parse(soldDesc);
          } catch {
            console.error('Error parsing SoldDescription string:', soldDesc);
            return { description: soldDesc };
          }
        }

        // Already parsed
        return soldDesc;
      };

      // SINGLE setData call with all data properly processed
      setData(prevData => {
        // Parse SoldDescription for Stock items
        const parsedStock = fetchedData?.Stock?.map(item => ({
          ...item,
          SoldDescription: parseSoldDescriptionFromBackend(item.SoldDescription)
        })) || [];

        const initializedCustomers = (fetchedData?.Customers || []).map(customer => ({
          ...customer,
          advancePayments: customer.advancePayments || [],
          totalAdvanceBalance: customer.totalAdvanceBalance || 0,
          advanceSummary: customer.advanceSummary || {
            totalPaid: 0,
            totalUsed: 0,
            totalRemaining: 0
          }
        }));

        return {
          Customers: initializedCustomers,
          CustomerTransactions: fetchedData?.CustomerTransactions || [],
          Stock: parsedStock, // Use the parsed stock with proper SoldDescription
          Suppliers: fetchedData?.Suppliers || [],
          SupplierTransactions: fetchedData?.SupplierTransactions || [],
          Girvi: fetchedData?.Girvi || [],
          GirviPayments: fetchedData?.GirviPayments || [],
          Roznama: fetchedData?.Roznama || [],
          CompanyDetails: fetchedData?.CompanyDetails || [],
          SchemeCard: fetchedData?.SchemeCard || [],
          // Handle SilverStock - it might be an object or might be missing
          SilverStock: fetchedData?.SilverStock && typeof fetchedData.SilverStock === 'object'
            ? {
              ...defaultSilverStock,
              ...fetchedData.SilverStock,
              timestamp: fetchedData.SilverStock.timestamp || new Date().toISOString()
            }
            : defaultSilverStock,
          // Handle SilverTransactions - it might be an array or might be missing
          SilverTransactions: Array.isArray(fetchedData?.SilverTransaction)
            ? fetchedData.SilverTransaction
            : []
        };
      });

      const loadSilverData = async () => {
        try {
          const [stockResponse, transactionsResponse] = await Promise.all([
            apiGetSilverStock(),
            apiGetSilverTransactions()
          ]);

          if (stockResponse?.success) {
            setData(prev => ({
              ...prev,
              SilverStock: {
                ...prev.SilverStock,
                ...stockResponse.data,
                timestamp: stockResponse.data.timestamp || new Date().toISOString()
              }
            }));
          }

          if (transactionsResponse?.success) {
            setData(prev => ({
              ...prev,
              SilverTransactions: transactionsResponse.data || []
            }));
          }
        } catch (error) {
          console.error("Failed to load silver data:", error);
        }
      };

      // After loading, initialize silver stock if it's all zeros
      const silverStock = fetchedData?.SilverStock || defaultSilverStock;
      const totalSilver = Object.values(silverStock).reduce((sum, val) => {
        if (typeof val === 'number') return sum + val;
        return sum;
      }, 0);

      if (totalSilver === 0) {
        console.log("Silver stock appears to be zero. Initializing...");
        // You could call initializeSilverStock here if needed
      }

      if (!hasSilverData) {
        console.log("Silver data not found in initial load, fetching separately...");
        await loadSilverData();
      }
    } catch (error) {
      console.error("Failed to load initial data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  // ========= ADVANCE PAYMENT FUNCTIONS =========

  // ========= ADVANCE PAYMENT FUNCTIONS FOR MANAGEMENT PAGE =========

  const withdrawAdvance = async (withdrawData) => {
    setLoading(true);
    try {
      const { customerId, customerName, amount, reason, date, paymentMode } = withdrawData;

      // Create paymentModes object for withdrawal (negative advance)
      const paymentModes = {};
      if (paymentMode === 'cash') paymentModes.cash = amount;
      else if (paymentMode === 'online') paymentModes.online = amount;
      else if (paymentMode === 'oldGold') paymentModes.oldGold = amount;
      else if (paymentMode === 'oldSilver') paymentModes.oldSilver = amount;
      else paymentModes.cash = amount; // default to cash

      // Call backend API - add advance with negative amount or use a withdrawal flag
      // Option 1: Add as negative advance
      const response = await apiAddAdvancePayment({
        customerId,
        customerName,
        paymentModes,
        totalAmount: -amount,  // Negative indicates withdrawal
        date: date ? new Date(date).toISOString() : new Date().toISOString(),
        remarks: `WITHDRAWAL: ${reason || "Advance withdrawal"}`,
        recordedBy: currentUser?.name || "System",
        status: "withdrawn"
      });

      if (response?.success) {
        await loadInitialData();

        return {
          success: true,
          data: response.data,
          message: `₹${amount} withdrawn successfully from advance balance`
        };
      } else {
        throw new Error(response?.error || "Failed to withdraw advance");
      }
    } catch (error) {
      console.error("Error withdrawing advance:", error);
      return { success: false, error: error.message, message: "Failed to withdraw advance" };
    } finally {
      setLoading(false);
    }
  };

  // Helper function to generate advance payment IDs
  // const generateAdvanceId = () => {
  //   return `ADV_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  // };

  const getCustomerAdvanceHistory = async (customerId) => {
    try {
      const result = await getCustomerAdvancePayments(customerId);
      console.log("Advance payments response for history:", result);

      if (result.success && result.data) {
        const history = [];

        result.data.forEach(advance => {
          // Add the advance payment itself
          if (advance.totalAmount > 0) {
            history.push({
              type: "add",
              date: new Date(advance.date).toLocaleDateString(),
              amount: advance.totalAmount,
              paymentBreakdown: advance.paymentModes,
              paymentMode: Object.keys(advance.paymentModes).join(", "),
              remarks: advance.remarks,
              id: advance.id,
              created_at: advance.created_at
            });
          }

          // Check where the usage records are stored
          // In your data, remainingBalances contains the usage array
          let usageRecords = [];

          if (advance.remainingBalances && Array.isArray(advance.remainingBalances)) {
            usageRecords = advance.remainingBalances;
          } else if (advance.usedInBills && Array.isArray(advance.usedInBills)) {
            usageRecords = advance.usedInBills;
          }

          // Add usage records
          if (usageRecords.length > 0) {
            usageRecords.forEach(use => {
              history.push({
                type: "used",
                date: new Date(use.date).toLocaleDateString(),
                amount: use.amountUsed,
                paymentBreakdown: use.modeBreakdown,
                remarks: `Used in Bill #${use.billNo || use.billId}`,
                billId: use.billId,
                billNo: use.billNo
              });
            });
          }
        });

        // Sort by date (newest first)
        history.sort((a, b) => new Date(b.date) - new Date(a.date));

        console.log("Processed history:", history);
        return history;
      }

      return [];
    } catch (error) {
      console.error("Error getting advance history:", error);
      return [];
    }
  };


  const addAdvancePayment = async (paymentData) => {
    setLoading(true);
    try {
      const {
        customerId,
        customerName,
        paymentModes,  // { cash, online, oldGold, oldSilver }
        totalAmount,
        date,
        remarks,
        recordedBy
      } = paymentData;

      // Call backend API
      const response = await apiAddAdvancePayment({
        customerId,
        customerName,
        paymentModes,
        totalAmount,
        date: date ? new Date(date).toISOString() : new Date().toISOString(),
        remarks: remarks || "",
        recordedBy: recordedBy || currentUser?.name || "System"
      });

      if (response?.success) {
        // Refresh data to get updated advance payments
        await loadInitialData();

        return {
          success: true,
          data: response.data,
          message: `Advance payment of ₹${totalAmount} recorded successfully`
        };
      } else {
        throw new Error(response?.error || "Failed to record advance payment");
      }
    } catch (error) {
      console.error("Error adding advance payment:", error);
      return { success: false, error: error.message, message: "Failed to record advance payment" };
    } finally {
      setLoading(false);
    }
  };

  const recordAdvancePayment = async (paymentData) => {
    setLoading(true);
    try {
      const result = await addAdvancePayment(paymentData);

      if (result.success) {
        // Update local customer data
        setData(prev => {
          const updatedCustomers = prev.Customers.map(customer => {
            if (customer.id === paymentData.customerId) {
              return {
                ...customer,
                totalAdvanceBalance: (customer.totalAdvanceBalance || 0) + paymentData.totalAmount
              };
            }
            return customer;
          });

          return { ...prev, Customers: updatedCustomers };
        });

        return result;
      }
      throw new Error(result.error || "Failed to record advance payment");
    } catch (error) {
      console.error("Error recording advance payment:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // In DataContext.js, add this function inside the DataProvider component

  const fetchCustomerAdvanceBalance = useCallback(async (customerId) => {
    setLoading(true);
    try {
      const result = await getCustomerAdvancePayments(customerId);
      console.log("Advance payments from backend:", result);

      if (result.success && result.data) {
        let totalRemaining = 0;
        const advancesByMode = {
          cash: 0,
          online: 0,
          cheque: 0,
          oldGold: 0,
          oldSilver: 0,
          other: 0
        };

        result.data.forEach(advance => {
          // Only consider active and partially_used advances
          if (advance.status === 'active' || advance.status === 'partially_used') {
            // Check if remainingBalances is an object (correct structure) or array (incorrect)
            let remainingBalances = {};

            if (advance.remainingBalances && typeof advance.remainingBalances === 'object') {
              // Check if it's an array (usage records) or actual remaining balances
              if (Array.isArray(advance.remainingBalances)) {
                // This is actually usedInBills data in the wrong field
                // We need to use the original paymentModes minus used amounts
                // For now, we'll use paymentModes as remaining since we don't have proper data
                console.warn("remainingBalances is an array, using paymentModes as fallback");
                remainingBalances = advance.paymentModes || {};
              } else {
                remainingBalances = advance.remainingBalances;
              }
            } else {
              remainingBalances = advance.paymentModes || {};
            }

            // Calculate total remaining and mode-wise breakdown
            Object.keys(remainingBalances).forEach(mode => {
              const amount = parseFloat(remainingBalances[mode]) || 0;
              totalRemaining += amount;

              // Map mode to our expected keys
              let mappedMode = mode;
              if (mode === 'oldGold') mappedMode = 'oldGold';
              else if (mode === 'oldSilver') mappedMode = 'oldSilver';
              else if (mode === 'online') mappedMode = 'online';
              else if (mode === 'cheque') mappedMode = 'cheque';
              else if (mode === 'cash') mappedMode = 'cash';
              else mappedMode = 'other';

              if (advancesByMode[mappedMode] !== undefined) {
                advancesByMode[mappedMode] += amount;
              } else {
                advancesByMode.other += amount;
              }
            });
          }
        });

        const resultData = {
          total: totalRemaining,
          byMode: advancesByMode,
          advances: result.data
        };

        console.log("Processed advance balance:", resultData);
        return resultData;
      }

      return {
        total: 0,
        byMode: { cash: 0, online: 0, cheque: 0, oldGold: 0, oldSilver: 0, other: 0 },
        advances: []
      };
    } catch (error) {
      console.error("Error fetching advance balance:", error);
      return {
        total: 0,
        byMode: { cash: 0, online: 0, cheque: 0, oldGold: 0, oldSilver: 0, other: 0 },
        advances: []
      };
    } finally {
      setLoading(false);
    }
  }, []);

  const applyAdvanceToBill = async (customerId, billId, billNo, usageAmounts, totalAmount) => {
    setLoading(true);
    try {
      const result = await advanceUseForBill(customerId, billId, billNo, usageAmounts, totalAmount);

      if (result.success) {
        // Update local customer advance balance
        setData(prev => {
          const updatedCustomers = prev.Customers.map(customer => {
            if (customer.id === customerId) {
              const newBalance = (customer.totalAdvanceBalance || 0) - totalAmount;
              return {
                ...customer,
                totalAdvanceBalance: newBalance > 0 ? newBalance : 0
              };
            }
            return customer;
          });

          return { ...prev, Customers: updatedCustomers };
        });

        return result;
      }
      throw new Error(result.error || "Failed to apply advance");
    } catch (error) {
      console.error("Error applying advance to bill:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getAllAdvancePaymentsList = async (filters = {}) => {
    setLoading(true);
    try {
      const result = await getAllAdvancePayments(filters);
      return result;
    } catch (error) {
      console.error("Error fetching advance payments:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const getAdvanceSummary = async (customerId) => {
    setLoading(true);
    try {
      const result = await getAdvancePaymentSummary(customerId);
      return result;
    } catch (error) {
      console.error("Error fetching advance summary:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  // Modify useEffect to also load silver data
  useEffect(() => {
    // Prevent multiple initial loads
    if (initialLoadRef.current) return;

    initialLoadRef.current = true;

    const initializeData = async () => {
      await loadInitialData();

      // Directly check the fetched data instead of using state
      // We'll check the data after loadInitialData completes
      console.log("Checking silver data...");

      // Since we just loaded the data, we can check if we need silver data
      // We'll use a separate approach
    };

    initializeData();
    console.log("DataContext initialized");
  }, [loadInitialData]);



  // const loadRoznamaForDate = async (date) => {
  //   try {
  //     const response = await getRoznamaByDate(date);
  //     if (response?.success) {
  //       setData(prev => ({
  //         ...prev,
  //         Roznama: response.data
  //       }));
  //       return response.data;
  //     }
  //   } catch (error) {
  //     console.error("Error loading roznama:", error);
  //   }
  //   return null;
  // };

  // const saveRoznamaToContext = async (roznamadata) => {
  //   setLoading(true);
  //   try {
  //     const response = await saveRoznama(roznamadata);

  //     if (response?.success) {
  //       // Update local state
  //       setData(prev => ({
  //         ...prev,
  //         Roznama: {
  //           ...prev.Roznama,
  //           ...response.data,
  //           id: response.data.id
  //         }
  //       }));

  //       return {
  //         success: true,
  //         data: response.data,
  //         message: "Roznama saved successfully"
  //       };
  //     } else {
  //       throw new Error(response?.error || "Failed to save roznama");
  //     }
  //   } catch (error) {
  //     console.error("Error saving roznama:", error);
  //     return {
  //       success: false,
  //       error: error.message,
  //       message: "Failed to save roznama"
  //     };
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // ========= COMPANY DETAILS FUNCTIONS =========
  const getCompanyDetailsFromContext = useCallback((filters = {}) => {
    let companies = data.CompanyDetails;

    // Apply filters
    if (filters.filterBy && filters.filterValue) {
      companies = companies.filter(company =>
        String(company[filters.filterBy] || '').toLowerCase().includes(String(filters.filterValue).toLowerCase())
      );
    }

    return companies;
  }, [data.CompanyDetails]);

  const bulkUpdateStockInContext = useCallback(async (items, action) => {
    if (!items || items.length === 0) {
      throw new Error('No items to update');
    }

    setLoading(true);

    // Prepare updates array
    const updates = items.map(item => {
      let soldStatus = item.sold;

      if (action === 'markAsSold') {
        soldStatus = "Yes";
      } else if (action === 'markAsUnsold') {
        soldStatus = "No";
      }

      return {
        id: item.id,
        sold: soldStatus
      };
    });

    // Store original state for rollback
    const originalStock = [...data.Stock];
    const currentTime = new Date().toISOString();

    // OPTIMISTIC UPDATE: Update UI immediately
    setData(prev => ({
      ...prev,
      Stock: prev.Stock.map(stockItem => {
        const update = updates.find(u => u.id === stockItem.id);
        if (update) {
          return {
            ...stockItem,
            sold: update.sold,
            updated_at: currentTime
          };
        }
        return stockItem;
      })
    }));

    try {
      // SINGLE API CALL for all updates
      const response = await apiBulkUpdateStock(updates);

      if (response?.success) {
        // Update successful - UI already updated
        // Update timestamps with server response if needed
        if (response.data?.timestamp) {
          setData(prev => ({
            ...prev,
            Stock: prev.Stock.map(stockItem => {
              const update = updates.find(u => u.id === stockItem.id);
              if (update) {
                return {
                  ...stockItem,
                  updated_at: response.data.timestamp
                };
              }
              return stockItem;
            })
          }));
        }

        return {
          success: true,
          results: response.data?.results || { success: updates.map(u => u.id), failed: [] },
          message: `Updated ${updates.length} item(s) successfully`
        };
      } else {
        // API failed - rollback to original state
        setData(prev => ({
          ...prev,
          Stock: originalStock
        }));

        throw new Error(response?.error || 'Bulk update failed');
      }

    } catch (error) {
      console.error('Bulk update failed:', error);

      // Rollback on error
      setData(prev => ({
        ...prev,
        Stock: originalStock
      }));

      return {
        success: false,
        error: error.message,
        message: 'Failed to update items'
      };
    } finally {
      setLoading(false);
    }
  }, [data.Stock]);

  /**
   * Bulk delete stock items with optimistic UI update
   * Single API call for all deletions
   */
  const bulkDeleteStockInContext = useCallback(async (items) => {
    if (!items || items.length === 0) {
      throw new Error('No items to delete');
    }

    setLoading(true);

    const itemIds = items.map(item => item.id);

    // Store original state for rollback
    const originalStock = [...data.Stock];

    // OPTIMISTIC UPDATE: Remove items from UI immediately
    setData(prev => ({
      ...prev,
      Stock: prev.Stock.filter(item => !itemIds.includes(item.id))
    }));

    try {
      // SINGLE API CALL for all deletions
      const response = await apiBulkDeleteStock(itemIds);

      if (response?.success) {
        // Deletion successful - UI already updated
        return {
          success: true,
          deletedCount: response.data?.deletedCount || itemIds.length,
          message: `Deleted ${itemIds.length} item(s) successfully`
        };
      } else {
        // API failed - restore all items
        setData(prev => ({
          ...prev,
          Stock: originalStock
        }));

        throw new Error(response?.error || 'Bulk delete failed');
      }

    } catch (error) {
      console.error('Bulk delete failed:', error);

      // Rollback on error
      setData(prev => ({
        ...prev,
        Stock: originalStock
      }));

      return {
        success: false,
        error: error.message,
        message: 'Failed to delete items'
      };
    } finally {
      setLoading(false);
    }
  }, [data.Stock]);

  const updateCompanyDetailsInContext = async (id, companyData) => {
    setLoading(true);
    try {
      const response = await updateCompanyDetails(id, companyData);

      if (response?.success === true) {
        const updatedCompany = response.data;
        setData(prev => ({
          ...prev,
          CompanyDetails: prev.CompanyDetails.map(company =>
            company.id === id ? { ...company, ...updatedCompany } : company
          )
        }));

        // If updating current user's details, update session
        if (currentUser?.id === id) {
          sessionManager.updateSession({
            ...currentUser,
            ...companyData
          });
          setCurrentUser(sessionManager.getSession());
        }

        return {
          success: true,
          data: updatedCompany,
          message: "Company details updated successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to update company details");
      }
    } catch (error) {
      console.error("Error updating company details:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to update company details"
      };
    } finally {
      setLoading(false);
    }
  };

  const addCompanyDetailsToContext = async (companyData) => {
    setLoading(true);
    try {
      const response = await apiAddCompanyDetails(companyData);

      if (response?.success === true) {
        const newCompany = response.data;
        setData(prev => ({
          ...prev,
          CompanyDetails: [...prev.CompanyDetails, newCompany]
        }));

        return {
          success: true,
          data: newCompany,
          message: "Company details added successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to add company details");
      }
    } catch (error) {
      console.error("Error adding company details:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to add company details"
      };
    } finally {
      setLoading(false);
    }
  };

  // ========= SILVER STOCK FUNCTIONS =========
  const getSilverStockFromContext = useCallback(() => {
    return data.SilverStock;
  }, [data.SilverStock]);

  const updateSilverStockInContext = async (silverType, netWeight, operation, description = '') => {
    setLoading(true);
    try {
      const response = await apiUpdateSilverStock(silverType, netWeight, operation, description);

      if (response?.success === true) {
        const updateData = response.data;

        // Update local silver stock
        setData(prev => ({
          ...prev,
          SilverStock: {
            ...prev.SilverStock,
            [silverType]: updateData.newWeight,
            timestamp: new Date().toISOString()
          }
        }));

        // Refresh transactions to include the new one
        await refreshSilverTransactions();

        return {
          success: true,
          data: updateData,
          message: `Silver stock ${operation === 'add' ? 'added to' : 'subtracted from'} successfully`
        };
      } else {
        throw new Error(response?.error || "Failed to update silver stock");
      }
    } catch (error) {
      console.error("Error updating silver stock:", error);
      return {
        success: false,
        error: error.message,
        message: `Failed to ${operation} silver stock`
      };
    } finally {
      setLoading(false);
    }
  };

  const bulkUpdateSilverStockInContext = async (updates) => {
    setLoading(true);
    try {
      const response = await apiBulkUpdateSilverStock(updates);

      if (response?.success === true) {
        const updateData = response.data;
        const today = new Date().toISOString().split('T')[0];
        const timestamp = new Date().toISOString();

        // Update local context immediately
        setData(prev => {
          const updatedStock = { ...prev.SilverStock };
          const transactionUpdates = [];

          // Process each update result
          updateData.results?.forEach(result => {
            if (result.success) {
              // Update silver stock
              updatedStock[result.silverType] = result.newWeight;

              // Collect transaction data
              const updateObj = updates.find(u => u.silverType === result.silverType);
              transactionUpdates.push({
                silverType: result.silverType,
                netWeight: result.weightChanged,
                operation: updateObj?.operation || 'subtract',
                description: updateObj?.description || 'Bulk update'
              });
            }
          });

          updatedStock.timestamp = timestamp;

          // Create dummy transaction entries
          const newTransactions = transactionUpdates.map(update => ({
            id: `dummy-${update.silverType}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            date: today,
            silverType: update.silverType,
            netWeight: Math.abs(update.netWeight),
            operation: update.operation,
            description: update.description,
            timestamp: timestamp,
            isLocal: true,
            synced: false
          }));

          // Update transactions in context
          const existingTransactions = prev.SilverTransactions || [];
          const updatedTransactions = [...newTransactions, ...existingTransactions];

          return {
            ...prev,
            SilverStock: updatedStock,
            SilverTransactions: updatedTransactions
          };
        });

        return {
          success: true,
          data: updateData,
          message: "Bulk silver update completed successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to perform bulk silver update");
      }
    } catch (error) {
      console.error("Error in bulk silver update:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to perform bulk silver update"
      };
    } finally {
      setLoading(false);
    }
  };

  const refreshSilverStock = async () => {
    setLoading(true);
    try {
      const response = await apiGetSilverStock();

      if (response?.success === true) {
        setData(prev => ({
          ...prev,
          SilverStock: response.data
        }));

        return {
          success: true,
          data: response.data,
          message: "Silver stock refreshed successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to refresh silver stock");
      }
    } catch (error) {
      console.error("Error refreshing silver stock:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to refresh silver stock"
      };
    } finally {
      setLoading(false);
    }
  };

  // ========= SILVER TRANSACTIONS FUNCTIONS =========
  const getSilverTransactionsFromContext = useCallback((filters = {}) => {
    let transactions = data.SilverTransactions;

    // Apply filters
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);

      transactions = transactions.filter(tx => {
        const txDate = new Date(tx.timestamp || tx.date);
        return txDate >= startDate && txDate <= endDate;
      });
    }

    if (filters.silverType) {
      transactions = transactions.filter(tx => tx.silverType === filters.silverType);
    }

    if (filters.operation) {
      transactions = transactions.filter(tx => tx.operation === filters.operation);
    }

    // Sort by date (most recent first)
    return transactions.sort((a, b) => {
      const dateA = new Date(a.timestamp || a.date);
      const dateB = new Date(b.timestamp || b.date);
      return dateB - dateA;
    });
  }, [data.SilverTransactions]);

  const refreshSilverTransactions = async (filters = {}) => {
    setLoading(true);
    try {
      const response = await apiGetSilverTransactions();

      if (response?.success === true) {
        setData(prev => ({
          ...prev,
          SilverTransactions: response.data || []
        }));

        return {
          success: true,
          data: response.data,
          message: "Silver transactions refreshed successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to refresh silver transactions");
      }
    } catch (error) {
      console.error("Error refreshing silver transactions:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to refresh silver transactions"
      };
    } finally {
      setLoading(false);
    }
  };

  const initializeSilverStockInContext = async () => {
    setLoading(true);
    try {
      const response = await apiInitializeSilverStock();

      if (response?.success === true) {
        setData(prev => ({
          ...prev,
          SilverStock: {
            ...prev.SilverStock,
            ...response.data.stock,
            timestamp: new Date().toISOString()
          }
        }));

        return {
          success: true,
          data: response.data,
          message: "Silver stock initialized successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to initialize silver stock");
      }
    } catch (error) {
      console.error("Error initializing silver stock:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to initialize silver stock"
      };
    } finally {
      setLoading(false);
    }
  };

  // ========= AUTHENTICATION FUNCTIONS =========
  const loginInContext = async (email, password) => {
    setLoading(true);
    try {
      const response = await apiLogin(email, password);

      if (response?.success === true) {
        const userData = response.data;
        // Update session using sessionManager
        sessionManager.createSession({
          id: userData.id,
          email: userData.email,
          name: userData.shopName || userData.ownerName,
          shopName: userData.shopName,
          ownerName: userData.ownerName
        });

        setCurrentUser(sessionManager.getSession());
        setIsAuthenticated(true);

        return {
          success: true,
          data: userData,
          message: "Login successful"
        };
      } else {
        throw new Error(response?.error || "Login failed");
      }
    } catch (error) {
      console.error("Error logging in:", error);
      return {
        success: false,
        error: error.message,
        message: "Login failed"
      };
    } finally {
      setLoading(false);
    }
  };

  const logoutInContext = () => {
    sessionManager.clearSession();
    setCurrentUser(null);
    setIsAuthenticated(false);
    return {
      success: true,
      message: "Logged out successfully"
    };
  };

  const sendPasswordResetOtpInContext = async (email) => {
    setLoading(true);
    try {
      const response = await sendPasswordResetOtp(email);
      return response;
    } catch (error) {
      console.error("Error sending password reset OTP:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const verifyPasswordResetOtpInContext = async (email, otp) => {
    setLoading(true);
    try {
      const response = await verifyPasswordResetOtp(email, otp);
      return response;
    } catch (error) {
      console.error("Error verifying password reset OTP:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const resetPasswordInContext = async (email, newPassword) => {
    setLoading(true);
    try {
      const response = await resetPassword(email, newPassword);
      return response;
    } catch (error) {
      console.error("Error resetting password:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  };

  const updatePasswordInContext = async (email, password) => {
    setLoading(true);
    try {
      const response = await apiUpdatePassword(email, password);

      if (response?.success === true) {
        // Update session if password update successful
        const userData = response.data;
        sessionManager.createSession({
          id: userData.id,
          email: userData.email,
          name: userData.shopName || userData.ownerName
        });
        setCurrentUser(sessionManager.getSession());

        return {
          success: true,
          data: userData,
          message: "Password updated successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to update password");
      }
    } catch (error) {
      console.error("Error updating password:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to update password"
      };
    } finally {
      setLoading(false);
    }
  };

  const closeRoznamaDayInContext = async (date, openingBalanceCash, openingBalanceBank, incomeTransactions, expenseTransactions, closingBalanceBank, closingBalanceCash, netProfit, totalIncome, totalExpense) => {
    setLoading(true);
    try {
      const response = await closeRoznamaDay(date, openingBalanceCash, openingBalanceBank, incomeTransactions, expenseTransactions, closingBalanceBank, closingBalanceCash, netProfit, totalIncome, totalExpense);
      console.log("Close Roznama Day Response:", response);
      if (response?.success) {
        //   // Load next day's roznama
        //   await loadRoznamaForDate(response.data.tomorrowDate);
        //
        return {
          success: true,
          data: response.data,
          message: "Day closed successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to close day");
      }
    } catch (error) {
      console.error("Error closing roznama day:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to close day"
      };
    } finally {
      setLoading(false);
    }
  };

  const recordPartialPayment = async (transactionId, paymentData) => {
    setLoading(true);
    try {
      // Find the transaction
      const transactionIndex = data.CustomerTransactions.findIndex(t => t.id === transactionId);

      if (transactionIndex === -1) {
        throw new Error(`Transaction with ID ${transactionId} not found`);
      }

      const transaction = { ...data.CustomerTransactions[transactionIndex] };

      // Calculate new payment details
      const newPaymentEntry = {
        id: generatePaymentId(),
        date: paymentData.date || new Date().toISOString(),
        amount: paymentData.amount,
        paymentModes: {
          cash: paymentData.cash || 0,
          online: paymentData.online || 0,
          cheque: paymentData.cheque || 0,
          other: paymentData.other || 0,
          advanceBreakup: paymentData.advanceBreakup || {}
        },
        note: paymentData.note || `Payment on ${new Date(paymentData.date || new Date()).toLocaleDateString()}`,
        recordedBy: paymentData.recordedBy || "System"
      };

      // Get existing payment history
      const existingPayments = transaction.paymentDetails?.paymentHistory || [];
      const updatedPayments = [...existingPayments, newPaymentEntry];

      // Calculate total paid
      const totalPaid = updatedPayments.reduce((sum, p) => sum + p.amount, 0);
      const originalAmount = transaction.transactionDetails?.originalBillAmount || transaction.summary?.grandTotal || 0;
      const newBalance = originalAmount - totalPaid;
      const isFullyPaid = newBalance <= 0;

      // Update transaction
      const updatedTransaction = {
        ...transaction,
        paymentDetails: {
          ...transaction.paymentDetails,
          paymentHistory: updatedPayments,
          totalPaymentDone: totalPaid,
          cash: (transaction.paymentDetails?.cash || 0) + (paymentData.cash || 0),
          online: (transaction.paymentDetails?.online || 0) + (paymentData.online || 0),
          cheque: (transaction.paymentDetails?.cheque || 0) + (paymentData.cheque || 0),
          other: (transaction.paymentDetails?.other || 0) + (paymentData.other || 0),
        },
        summary: {
          ...transaction.summary,
          paymentDone: totalPaid,
          pendingAmount: newBalance > 0 ? newBalance : 0
        },
        transactionDetails: {
          ...transaction.transactionDetails,
          balanceDue: newBalance > 0 ? newBalance : 0,
          isFullyPaid: isFullyPaid,
          paymentStatus: isFullyPaid ? "paid" : "partial",
          lastPaymentDate: newPaymentEntry.date,
          lastPaymentAmount: newPaymentEntry.amount
        },
        updated_at: new Date().toISOString()
      };

      // Update customer balance if needed
      let updatedCustomers = [...data.Customers];
      if (transaction.customer) {
        const customerIndex = updatedCustomers.findIndex(c => c.id === transaction.customer);
        if (customerIndex !== -1) {
          const currentBalance = parseFloat(updatedCustomers[customerIndex].currentBalance) || 0;
          const paymentAmount = paymentData.amount;

          updatedCustomers[customerIndex] = {
            ...updatedCustomers[customerIndex],
            currentBalance: currentBalance - paymentAmount,
            updated_at: new Date().toISOString()
          };
        }
      }

      // Update Roznama with this payment
      let updatedRoznama = [...data.Roznama];
      const todayDate = getTodayISTDate();
      const customerName = updatedCustomers.find(c => c.id === transaction.customer)?.name || "Unknown Customer";

      const roznamaPaymentEntry = {
        id: `payment-${newPaymentEntry.id}`,
        date: todayDate,
        type: "income",
        billNumber: `BILL-${transaction.transactionDetails?.billNo || '0000'}`,
        customerSupplier: customerName,
        description: `Partial payment received - Bill #${transaction.transactionDetails?.billNo}`,
        amount: paymentData.amount,
        totalReceived: paymentData.amount,
        bank: (paymentData.online || 0) + (paymentData.cheque || 0),
        cash: paymentData.cash || 0,
        totalPending: newBalance,
        paymentMode: "partial",
        remark: paymentData.note || "Partial payment",
        transactionType: "customer",
        isAutoGenerated: true,
        referenceId: transactionId,
        paymentId: newPaymentEntry.id,
        created_at: new Date().toISOString()
      };

      // Update Roznama
      const roznamaIndex = updatedRoznama.findIndex(r => {
        try {
          const rDate = new Date(r.date).toISOString().split('T')[0];
          return rDate === todayDate;
        } catch (error) {
          return false;
        }
      });

      if (roznamaIndex >= 0) {
        const existingRoznama = { ...updatedRoznama[roznamaIndex] };
        const existingIncomeTransactions = Array.isArray(existingRoznama.incomeTransactions)
          ? existingRoznama.incomeTransactions
          : [];

        existingRoznama.incomeTransactions = [...existingIncomeTransactions, roznamaPaymentEntry];
        existingRoznama.totalIncome = (parseFloat(existingRoznama.totalIncome) || 0) + paymentData.amount;
        existingRoznama.netProfit = existingRoznama.totalIncome - (parseFloat(existingRoznama.totalExpense) || 0);
        existingRoznama.closingBalanceCash = (parseFloat(existingRoznama.closingBalanceCash) || 0) + (paymentData.cash || 0);
        existingRoznama.closingBalanceBank = (parseFloat(existingRoznama.closingBalanceBank) || 0) + (paymentData.online || 0) + (paymentData.cheque || 0);
        existingRoznama.updated_at = new Date().toISOString();

        updatedRoznama[roznamaIndex] = existingRoznama;
      } else {
        const lastRoznama = updatedRoznama.length > 0 ? updatedRoznama[updatedRoznama.length - 1] : null;
        const newRoznama = {
          id: `temp-${Date.now()}`,
          date: todayDate,
          openingBalanceCash: lastRoznama?.closingBalanceCash || 0,
          openingBalanceBank: lastRoznama?.closingBalanceBank || 0,
          closingBalanceCash: (lastRoznama?.closingBalanceCash || 0) + (paymentData.cash || 0),
          closingBalanceBank: (lastRoznama?.closingBalanceBank || 0) + (paymentData.online || 0) + (paymentData.cheque || 0),
          incomeTransactions: [roznamaPaymentEntry],
          expenseTransactions: [],
          netProfit: paymentData.amount,
          totalIncome: paymentData.amount,
          totalExpense: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        updatedRoznama.push(newRoznama);
      }

      // Update all data in a single state update
      setData(prev => {
        const updatedTransactions = [...prev.CustomerTransactions];
        updatedTransactions[transactionIndex] = updatedTransaction;

        return {
          ...prev,
          Customers: updatedCustomers,
          CustomerTransactions: updatedTransactions,
          Roznama: updatedRoznama
        };
      });

      // Also update the backend via API
      const response = await updateCustomerTransaction(transactionId, updatedTransaction);

      if (response?.success) {
        return {
          success: true,
          data: {
            transaction: updatedTransaction,
            payment: newPaymentEntry,
            newBalance: newBalance,
            isFullyPaid: isFullyPaid
          },
          message: isFullyPaid ? "Payment completed! Bill is now fully paid." : "Partial payment recorded successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to update backend");
      }

    } catch (error) {
      console.error("Error recording partial payment:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to record payment"
      };
    } finally {
      setLoading(false);
    }
  };

  // Get payment history for a specific transaction
  const getPaymentHistory = (transactionId) => {
    const transaction = data.CustomerTransactions.find(t => t.id === transactionId);
    if (!transaction) return [];

    return transaction.paymentDetails?.paymentHistory || [];
  };

  // Get all pending payments (for dashboard/reports)
  const getPendingPayments = (filters = {}) => {
    let pendingTransactions = data.CustomerTransactions.filter(t => {
      const isPending = t.transactionDetails?.balanceDue > 0 && !t.transactionDetails?.isFullyPaid && t.transactionDetails?.type;
      if (!isPending) return false;

      // Apply filters
      if (filters.customerId && t.customer !== filters.customerId) return false;
      if (filters.startDate) {
        const txDate = new Date(t.date);
        const startDate = new Date(filters.startDate);
        if (txDate < startDate) return false;
      }
      if (filters.endDate) {
        const txDate = new Date(t.date);
        const endDate = new Date(filters.endDate);
        if (txDate > endDate) return false;
      }

      return true;
    });

    // Sort by date (oldest first for pending payments)
    pendingTransactions.sort((a, b) => new Date(a.date) - new Date(b.date));

    return pendingTransactions.map(t => ({
      id: t.id,
      billNo: t.transactionDetails?.billNo,
      customerId: t.customer,
      customerName: data.Customers.find(c => c.id === t.customer)?.name || "Unknown",
      date: t.date,
      originalAmount: t.transactionDetails?.originalBillAmount || t.summary?.grandTotal,
      paidAmount: t.paymentDetails?.totalPaymentDone || 0,
      balanceDue: t.transactionDetails?.balanceDue,
      lastPaymentDate: t.paymentDetails?.paymentHistory?.[t.paymentDetails.paymentHistory.length - 1]?.date,
      paymentHistory: t.paymentDetails?.paymentHistory || []
    }));
  };

  // Get customer balance summary
  const getCustomerBalanceSummary = (customerId) => {
    const customerTransactions = data.CustomerTransactions.filter(t => t.customer === customerId);

    const totalBilled = customerTransactions.reduce((sum, t) =>
      sum + (t.transactionDetails?.originalBillAmount || t.summary?.grandTotal || 0), 0);

    const totalPaid = customerTransactions.reduce((sum, t) =>
      sum + (t.paymentDetails?.totalPaymentDone || 0), 0);

    const totalBalance = customerTransactions.reduce((sum, t) =>
      sum + (t.transactionDetails?.balanceDue || 0), 0);

    const pendingTransactions = customerTransactions.filter(t =>
      t.transactionDetails?.balanceDue > 0 && !t.transactionDetails?.isFullyPaid
    );

    return {
      customerId,
      totalBilled,
      totalPaid,
      totalBalance,
      pendingCount: pendingTransactions.length,
      pendingTransactions: pendingTransactions.map(t => ({
        id: t.id,
        billNo: t.transactionDetails?.billNo,
        date: t.date,
        amount: t.transactionDetails?.originalBillAmount || t.summary?.grandTotal,
        paid: t.paymentDetails?.totalPaymentDone || 0,
        balance: t.transactionDetails?.balanceDue,
        lastPaymentDate: t.paymentDetails?.paymentHistory?.[t.paymentDetails.paymentHistory.length - 1]?.date
      }))
    };
  };

  // Helper function to generate unique payment IDs
  const generatePaymentId = () => {
    return `pay_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Combined transaction and stock update
  const addCustomerTransactionAndUpdateStock = async (transactionData, stockItems) => {
    setLoading(true);
    try {
      console.log("Adding transaction and updating stock:", { transactionData, stockItems });

      const response = await apiAddCustomerTransactionAndUpdateStock(transactionData, stockItems);
      console.log("Full API Response:", response);

      if (response?.success) {
        console.log(response?.data)
        // The response structure from your Google Apps Script
        const newTransactionId = response?.data?.id;
        const customerUpdateInfo = response?.data?.customerUpdate;
        const timestamps = response?.data?.timestamps;
        // const silverUpdates = response?.data?.silverUpdates;
        // const roznamaUpdate = response?.data?.roznamaUpdate;
        const currentTime = new Date().toISOString();

        // Create the new transaction object
        // In addCustomerTransactionAndUpdateStock function, update the transaction creation part:

        const newTransaction = {
          id: newTransactionId,
          customer: transactionData.customer,
          date: transactionData.date.toString().charAt(0) === "'" ? transactionData?.date?.toString().slice(1) : transactionData.date.toString(),
          items: transactionData.items,
          calculatedTotals: transactionData.calculatedTotals,
          paymentDetails: {
            ...transactionData.paymentDetails,
            // Add payment history array
            paymentHistory: [{
              id: generatePaymentId(),
              date: new Date().toISOString(),
              amount: transactionData.paymentDetails.totalPaymentDone || 0,
              paymentModes: {
                cash: transactionData.paymentDetails.cash || 0,
                online: transactionData.paymentDetails.online || 0,
                cheque: transactionData.paymentDetails.cheque || 0,
                other: transactionData.paymentDetails.other || 0,
                advanceBreakup: transactionData.paymentDetails.advancePayment?.breakup || {}
              },
              note: "Initial payment",
              recordedBy: transactionData.metaDetails?.handledBy || "System"
            }]
          },
          summary: {
            ...transactionData.summary,
            pendingAmount: transactionData.summary.pendingAmount || 0
          },
          transactionDetails: {
            ...transactionData.transactionDetails,
            originalBillAmount: transactionData.summary.grandTotal,
            balanceDue: transactionData.summary.pendingAmount || 0,
            isFullyPaid: (transactionData.summary.pendingAmount || 0) === 0,
            paymentStatus: (transactionData.summary.pendingAmount || 0) === 0 ? "paid" : "partial"
          },
          metaDetails: transactionData.metaDetails,
          created_at: timestamps?.created_at || currentTime,
          updated_at: timestamps?.updated_at || currentTime
        };
        const convertToYYYYMMDD = (dateInput) => {
          try {
            // Handle null/undefined/empty
            if (!dateInput && dateInput !== 0) {
              console.warn('Empty date input received');
              return null;
            }

            // Convert to string and trim
            let dateStr = String(dateInput).trim();
            console.log('Input after string conversion:', JSON.stringify(dateStr));

            // Remove leading apostrophe if present
            if (dateStr.startsWith("'")) {
              dateStr = dateStr.substring(1);
              console.log('After removing apostrophe:', JSON.stringify(dateStr));
            }

            // If it's already in YYYY-MM-DD format, return as-is
            if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
              console.log('Already in YYYY-MM-DD format:', dateStr);
              return dateStr;
            }

            let date;

            // Handle DD/MM/YYYY format (like '13/1/2026')
            if (dateStr.includes('/')) {
              const parts = dateStr.split('/');
              console.log('Splitting by /:', parts);

              if (parts.length === 3) {
                // Parse as DD/MM/YYYY
                const day = parseInt(parts[0], 10);
                const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
                const year = parseInt(parts[2], 10);

                console.log(`Parsed as DD/MM/YYYY: day=${day}, month=${month}, year=${year}`);

                // Create date in local timezone
                date = new Date(year, month, day);

                // Validate the date
                if (date.getDate() !== day || date.getMonth() !== month || date.getFullYear() !== year) {
                  throw new Error('Invalid date after parsing');
                }
              } else {
                throw new Error(`Invalid date format: ${dateStr}. Expected DD/MM/YYYY`);
              }
            } else {
              // Try parsing as a standard date string
              date = new Date(dateStr);
            }

            if (isNaN(date.getTime())) {
              throw new Error(`Invalid date: ${dateStr}`);
            }

            // Get local date components
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            const result = `${year}-${month}-${day}`;
            console.log('Normalized result:', result);
            return result;

          } catch (error) {
            console.error('Error normalizing date:', error.message, 'Input:', JSON.stringify(dateInput));
            return null;
          }
        };

        // Normalize the transaction date (similar to backend)
        // const normalizeDate = (dateStr) => {
        //   try {
        //     const date = new Date(dateStr);
        //     return date.toISOString().split('T')[0]; // YYYY-MM-DD format
        //   } catch (error) {
        //     return dateStr;
        //   }
        // };

        // Update all related data in a single state update
        setData(prev => {
          // 1. Add the new transaction
          const updatedTransactions = [...prev.CustomerTransactions, newTransaction];

          // 2. Update stock items (mark as sold and add sold description)
          const updatedStock = prev.Stock.map(stockItem => {
            if (stockItems.includes(stockItem.id)) {
              // Create sold description object
              const soldDescription = {
                sold_date: transactionData.date,
                sold_bill_no: transactionData.transactionDetails?.billNo || 0,
                customer_id: transactionData.customer,
                transaction_id: newTransactionId,
                customer_name: transactionData.customerName || "Unknown Customer"
              };

              return {
                ...stockItem,
                sold: "Yes",
                soldDescription: soldDescription, // Add this line
                updated_at: currentTime
              };
            }
            return stockItem;
          });

          console.log("Stock item not in sold list:", updatedStock);
          // 3. Update customer balance if customerUpdateInfo exists
          let updatedCustomers = prev.Customers;
          if (customerUpdateInfo?.customerId) {
            updatedCustomers = prev.Customers.map(customer => {
              if (customer.id === customerUpdateInfo.customerId) {
                return {
                  ...customer,
                  currentBalance: customerUpdateInfo.newBalance || 0,
                  updated_at: currentTime
                };
              }
              return customer;
            });
          }

          // 4. Update silver stock if there are manual silver items
          let updatedSilverStock = { ...prev.SilverStock };
          let updatedSilverTransactions = [...(prev.SilverTransactions || [])];

          // Check if there are manual silver items in the transaction
          if (transactionData.items?.some(item => item.manualItemDetails?.metal === 'Silver' || item.manualItemDetails?.metal === 'Gold')) {
            console.log("Processing manual silver items from transaction data");

            const silverResults = [];

            // Process each manual silver item
            transactionData.items.forEach(item => {
              if (item.manualItemDetails?.metal === 'Silver') {
                const silverDetails = item.manualItemDetails;
                const silverType = silverDetails.silverType || 'silver';
                const weight = parseFloat(silverDetails.weight) || 0;

                if (weight > 0) {
                  // Calculate new silver stock weight
                  const currentWeight = parseFloat(updatedSilverStock[silverType]) || 0;
                  const newWeight = Math.max(0, currentWeight - weight); // Subtract weight, don't go below 0

                  // Update silver stock
                  updatedSilverStock[silverType] = newWeight;

                  // Collect result for tracking
                  silverResults.push({
                    silverType: silverType,
                    success: true,
                    weightChanged: weight,
                    previousWeight: currentWeight,
                    newWeight: newWeight
                  });

                  console.log(`Silver update: ${silverType} -${weight}g (${currentWeight}g → ${newWeight}g)`);
                }
              }
              if (item.manualItemDetails?.metal === 'Gold') {
                const silverDetails = item.manualItemDetails;
                const silverType = silverDetails.silverType || 'silver';
                const weight = parseFloat(silverDetails.weight) || 0;
                console.log(`Updating gold stock: ${silverType}, Current: , Weight to subtract: ${weight}`);

                if (weight > 0) {
                  // Calculate new silver stock weight
                  const currentWeight = parseFloat(updatedSilverStock[silverType]) || 0;
                  const newWeight = Math.max(0, currentWeight - weight); // Subtract weight, don't go below 0
                  console.log(`Updating gold stock: ${silverType}, Current: ${currentWeight}, Weight to subtract: ${weight}`);
                  // Update silver stock
                  updatedSilverStock[silverType] = newWeight;

                  // Collect result for tracking
                  silverResults.push({
                    silverType: silverType,
                    success: true,
                    weightChanged: weight,
                    previousWeight: currentWeight,
                    newWeight: newWeight
                  });

                  console.log(`Silver update: ${silverType} -${weight}g (${currentWeight}g → ${newWeight}g)`);
                }
              }
            });

            updatedSilverStock.timestamp = currentTime;

            // Create local silver transaction entries
            const today = currentTime.split('T')[0];

            const newSilverTransactions = silverResults
              .filter(result => result.success)
              .map(result => {
                // Find the corresponding manual silver item from transaction
                const silverItem = transactionData.items?.find(item =>
                  item.manualItemDetails?.silverType === result.silverType
                );

                return {
                  id: `silver-tx-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                  date: today,
                  silverType: result.silverType,
                  netWeight: result.weightChanged,
                  operation: 'subtract',
                  description: silverItem?.manualItemDetails?.name || `Silver item sold to ${transactionData.customerName || 'Customer'}`,
                  timestamp: currentTime,
                  billNo: transactionData.transactionDetails?.billNo,
                  customerName: transactionData.customerName,
                  itemName: silverItem?.manualItemDetails?.name || `Silver Item`,
                  isLocal: true,
                  synced: false
                };
              });

            // Add new silver transactions to the beginning of the array
            updatedSilverTransactions = [...newSilverTransactions, ...updatedSilverTransactions];

            console.log(`Created ${newSilverTransactions.length} silver transaction records`);
          }

          // 5. UPDATE ROZNAMA - MIRRORING BACKEND LOGIC
          let updatedRoznama = [...prev.Roznama];

          // Get customer name for roznama entry
          const customerName = prev.Customers.find(c => c.id === transactionData.customer)?.name || "Unknown Customer";

          // Create roznama income transaction (matching backend structure)
          const calculateRoznamaTransactionFromCustomer = (transactionData, customerName) => {
            const cashPayment = parseFloat(transactionData.paymentDetails?.cash || 0) +
              parseFloat(transactionData.paymentDetails?.advancePayment?.breakup?.cash || 0);

            const bankPayment =
              parseFloat(transactionData.paymentDetails?.cheque || 0) +
              parseFloat(transactionData.paymentDetails?.online || 0) +
              parseFloat(transactionData.paymentDetails?.advancePayment?.breakup?.online || 0);

            const paymentMode = bankPayment === 0 && cashPayment > 0 ? "cash" :
              bankPayment > 0 && cashPayment === 0 ? "bank" : "split";

            const todayDate = getTodayISTDate();

            return {
              id: newTransactionId,
              date: todayDate, // Use today's date instead of transaction date
              type: "income",
              billNumber: `CUST-${transactionData.transactionDetails?.billNo || '0000'}`,
              customerSupplier: customerName || "Unknown Customer",
              description: transactionData.items
                ?.map((item) => item.itemName || item.name || "Item")
                .join(", ") || "Customer Sale",
              amount: transactionData.summary?.grandTotal || 0,
              totalReceived: transactionData.paymentDetails?.totalPaymentDone || 0,
              bank: bankPayment || 0,
              cash: cashPayment || 0,
              totalPending: parseFloat(transactionData.summary?.pendingAmount || 0).toFixed(0),
              paymentMode: paymentMode,
              remark: "Auto-generated from customer transaction",
              transactionType: "customer",
              isAutoGenerated: true,
              referenceId: newTransactionId,
              created_at: currentTime
            };
          };

          const roznamaIncomeTransaction = calculateRoznamaTransactionFromCustomer(transactionData, customerName);
          console.log(roznamaIncomeTransaction)

          const todayDate = getTodayISTDate();

          // Find roznama for TODAY'S date
          const roznamaIndex = updatedRoznama.findIndex(r => {
            try {
              const roznamaDate = convertToYYYYMMDD(r.date);
              console.log('Roznama date:', r.date, '->', roznamaDate);
              console.log('Today date:', todayDate);
              return roznamaDate === todayDate;
            } catch (error) {
              return false;
            }
          });
          console.log(roznamaIndex)
          // Get or calculate opening balance (previous day's closing)
          let openingBalanceCash = 0;
          let openingBalanceBank = 0;

          if (roznamaIndex >= 0) {
            // Existing roznama - use its opening balance
            openingBalanceCash = updatedRoznama[roznamaIndex].openingBalanceCash || 0;
            openingBalanceBank = updatedRoznama[roznamaIndex].openingBalanceBank || 0;
          } else {
            // New roznama - find previous day's closing balance (simplified)
            // In real scenario, you'd find the last roznama entry
            const lastRoznama = updatedRoznama.length > 0
              ? updatedRoznama[updatedRoznama.length - 1]
              : null;

            openingBalanceCash = lastRoznama?.closingBalanceCash || 0;
            openingBalanceBank = lastRoznama?.closingBalanceBank || 0;
          }

          if (roznamaIndex >= 0) {
            // Update existing roznama
            const existingRoznama = { ...updatedRoznama[roznamaIndex] };

            // Parse existing transactions
            const existingIncomeTransactions = Array.isArray(existingRoznama.incomeTransactions)
              ? existingRoznama.incomeTransactions
              : [];

            // Add new income transaction
            existingRoznama.incomeTransactions = [...existingIncomeTransactions, roznamaIncomeTransaction];

            console.log(existingRoznama)

            // Recalculate totals (mirroring backend logic)
            const transactionAmount = roznamaIncomeTransaction.totalReceived || 0;
            const cashReceived = roznamaIncomeTransaction.cash || 0;
            const bankReceived = roznamaIncomeTransaction.bank || 0;

            // Update totals
            existingRoznama.totalIncome = (parseFloat(existingRoznama.totalIncome) || 0) + transactionAmount;
            existingRoznama.totalExpense = parseFloat(existingRoznama.totalExpense) || 0;
            existingRoznama.netProfit = existingRoznama.totalIncome - existingRoznama.totalExpense;

            // Update closing balances
            existingRoznama.closingBalanceCash = (parseFloat(existingRoznama.closingBalanceCash) || 0) + cashReceived;
            existingRoznama.closingBalanceBank = (parseFloat(existingRoznama.closingBalanceBank) || 0) + bankReceived;
            existingRoznama.updated_at = currentTime;
            console.log(existingRoznama)
            updatedRoznama[roznamaIndex] = existingRoznama;
          } else {
            // Create new roznama entry
            const newRoznamaId = `temp-${Date.now()}`;

            const cashReceived = roznamaIncomeTransaction.cash || 0;
            const bankReceived = roznamaIncomeTransaction.bank || 0;
            const transactionAmount = roznamaIncomeTransaction.totalReceived || 0;

            // Calculate totals
            const totalIncome = transactionAmount;
            const totalExpense = 0;
            const netProfit = totalIncome - totalExpense;
            const newRoznama = {
              id: newRoznamaId,
              date: todayDate,
              openingBalanceCash: openingBalanceCash,
              openingBalanceBank: openingBalanceBank,
              closingBalanceCash: openingBalanceCash + cashReceived,
              closingBalanceBank: openingBalanceBank + bankReceived,
              incomeTransactions: [roznamaIncomeTransaction],
              expenseTransactions: [],
              netProfit: netProfit,
              totalIncome: totalIncome,
              totalExpense: totalExpense,
              created_at: currentTime,
              updated_at: currentTime
            };

            updatedRoznama.push(newRoznama);
          }

          // Sort roznama by date (most recent first)
          updatedRoznama.sort((a, b) => {
            const dateA = new Date(a.date);
            const dateB = new Date(b.date);
            return dateB - dateA;
          });

          console.log("State updated:", {
            newTransactionId: newTransactionId,
            updatedStockItems: stockItems.length,
            customerUpdated: !!customerUpdateInfo?.balanceUpdated,
            silverItemsProcessed: transactionData.items?.filter(item => item.manualItemDetails?.metal === 'Silver').length || 0,
            roznamaUpdated: true,
            updatedRoznama
          });

          return {
            ...prev,
            Customers: updatedCustomers,
            CustomerTransactions: updatedTransactions,
            Stock: updatedStock,
            SilverStock: updatedSilverStock,
            SilverTransactions: updatedSilverTransactions,
            Roznama: updatedRoznama  // Add this line
          };
        });

        return {
          success: true,
          data: {
            transaction: newTransaction,
            customerUpdate: customerUpdateInfo,
            silverUpdates: {
              performed: transactionData.items?.some(item => item.manualItemDetails?.metal === 'Silver') || false,
              count: transactionData.items?.filter(item => item.manualItemDetails?.metal === 'Silver').length || 0
            },
            roznamaUpdate: {
              updated: true,
              transactionType: "income"
            }
          },
          message: response?.data?.message || "Transaction added successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to add transaction and update stock");
      }
    } catch (error) {
      console.error("Error adding transaction and updating stock:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to add transaction and update stock"
      };
    } finally {
      setLoading(false);
    }
  };

  // Helper function to sort data alphabetically by name (for arrays that have name property)
  const sortByName = (dataArray) => {
    if (!Array.isArray(dataArray)) return dataArray;
    return [...dataArray].sort((a, b) =>
      a.name?.localeCompare(b.name || '', undefined, { sensitivity: 'base' })
    );
  };

  // Generic update function for any data type
  const updateData = useCallback((dataType, newData) => {
    console.log(`Updating ${dataType} with ${newData.length || newData} items`);
    setData(prev => ({
      ...prev,
      [dataType]: newData
    }));
  }, []);

  // Generic CRUD operations template
  const createItem = async (dataType, itemData, serviceFunction) => {
    setLoading(true);
    try {
      console.log(`Adding ${dataType}:`, itemData);
      const response = await serviceFunction(itemData);
      console.log(`${dataType} add response:`, response);
      if (response?.success) {
        const newItem = response.data;

        // Update the specific data type
        setData(prev => {
          const updatedArray = [...prev[dataType], newItem];
          const finalArray = dataType === 'Customers' || dataType === 'Suppliers'
            ? sortByName(updatedArray)
            : updatedArray;

          console.log(`${dataType} updated, new count: ${finalArray.length}`);
          return {
            ...prev,
            [dataType]: finalArray
          };
        });

        return {
          success: true,
          data: newItem,
          message: `${getDataTypeLabel(dataType)} added successfully`
        };
      } else {
        throw new Error(response?.error || `Failed to add ${dataType}`);
      }
    } catch (error) {
      console.error(`Error adding ${dataType}:`, error);
      return {
        success: false,
        error: error.message,
        message: `Failed to add ${getDataTypeLabel(dataType)}`
      };
    } finally {
      setLoading(false);
    }
  };

  const updateItem = async (dataType, id, itemData, serviceFunction) => {
    setLoading(true);
    try {
      console.log(`Updating ${dataType} ID ${id}:`, itemData);
      const response = await serviceFunction(id, itemData);
      console.log(response);

      if (response?.success) {
        const updatedItem = response.data;
        setData(prev => {
          const updatedArray = prev[dataType].map(item =>
            item.id === id ? { ...item, ...updatedItem } : item
          );
          const finalArray = dataType === 'Customers' || dataType === 'Suppliers'
            ? sortByName(updatedArray)
            : updatedArray;

          return {
            ...prev,
            [dataType]: finalArray
          };
        });

        return {
          success: true,
          data: updatedItem,
          message: `${getDataTypeLabel(dataType)} updated successfully`
        };
      } else {
        throw new Error(response?.error || `Failed to update ${dataType}`);
      }
    } catch (error) {
      console.error(`Error updating ${dataType}:`, error);
      return {
        success: false,
        error: error.message,
        message: `Failed to update ${getDataTypeLabel(dataType)}`
      };
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async (dataType, id, serviceFunction) => {
    setLoading(true);
    try {
      console.log(`Deleting ${dataType} ID ${id}`);
      const response = await serviceFunction(id);

      if (response?.success) {
        setData(prev => {
          const updatedArray = prev[dataType].filter(item => item.id !== id);
          console.log(`${dataType} deleted, new count: ${updatedArray.length}`);
          return {
            ...prev,
            [dataType]: updatedArray
          };
        });

        return {
          success: true,
          message: `${getDataTypeLabel(dataType)} deleted successfully`
        };
      } else {
        throw new Error(response?.error || `Failed to delete ${dataType}`);
      }
    } catch (error) {
      console.error(`Error deleting ${dataType}:`, error);
      return {
        success: false,
        error: error.message,
        message: `Failed to delete ${getDataTypeLabel(dataType)}`
      };
    } finally {
      setLoading(false);
    }
  };

  // Helper function for data type labels
  const getDataTypeLabel = (dataType) => {
    const labels = {
      Customers: 'Customer',
      CustomerTransactions: 'Transaction',
      Stock: 'Stock Item',
      Suppliers: 'Supplier',
      SupplierTransactions: 'Supplier Transaction',
      Girvi: 'Girvi',
      GirviPayments: 'Girvi Payment'
    };
    return labels[dataType] || dataType;
  };

  // Get data by type with optional filter
  const getDataByType = useCallback((dataType, filterFn) => {
    const dataArray = data[dataType] || [];
    return filterFn ? dataArray.filter(filterFn) : dataArray;
  }, [data]);

  // Add new data type dynamically
  const addDataType = useCallback((dataType, initialValue = []) => {
    if (!data[dataType]) {
      console.log(`Adding new data type: ${dataType}`);
      setData(prev => ({
        ...prev,
        [dataType]: initialValue
      }));
    }
  }, [data]);

  const calculateTotal = (transactions, type = 'all') => {
    if (!Array.isArray(transactions)) return 0;

    return transactions.reduce((sum, t) => {
      const amount = parseFloat(t.amount) || 0;
      const cash = parseFloat(t.cash) || 0;
      const bank = parseFloat(t.bank) || 0;

      if (type === 'cash') return sum + cash;
      if (type === 'bank') return sum + bank;
      return sum + amount;
    }, 0);
  };

  // const getPreviousDayClosingBalance = (dateStr, roznamaData, balanceType) => {
  //   // Sort roznama by date (most recent first)
  //   const sortedRoznama = [...roznamaData].sort((a, b) => {
  //     const dateA = new Date(a.date);
  //     const dateB = new Date(b.date);
  //     return dateB - dateA;
  //   });

  //   const currentDate = new Date(dateStr);

  //   // Find roznama entries before current date
  //   const previousRoznama = sortedRoznama.find(r => {
  //     const roznamaDate = new Date(r.date);
  //     return roznamaDate < currentDate;
  //   });

  //   if (!previousRoznama) return 0;

  //   if (balanceType === 'cash') {
  //     return parseFloat(previousRoznama.closingBalanceCash) || 0;
  //   } else if (balanceType === 'bank') {
  //     return parseFloat(previousRoznama.closingBalanceBank) || 0;
  //   }

  //   return 0;
  // };

  // Specific CRUD functions for each data type
  // Customers
  const addCustomerToContext = (customerData) =>
    createItem('Customers', customerData, addCustomer);

  const updateCustomerInContext = (id, customerData) =>
    updateItem('Customers', id, customerData, updateCustomer);

  const deleteCustomerFromContext = (id) =>
    deleteItem('Customers', id, deleteCustomer);

  // Scheme Card
  const addSchemeCardToContext = (schemeCardData) =>
    createItem('SchemeCard', schemeCardData, addSchemeCard);

  const updateSchemeCardInContext = (id, schemeCardData) =>
    updateItem('SchemeCard', id, schemeCardData, updateSchemeCard);

  const deleteSchemeCardFromContext = (id) =>
    deleteItem('SchemeCard', id, deleteSchemeCard);

  // Customer Transactions
  const addCustomerTransactionToContext = (transactionData) =>
    createItem('CustomerTransactions', transactionData, addCustomerTransaction);

  const updateCustomerTransactionInContext = (id, transactionData) =>
    updateItem('CustomerTransactions', id, transactionData, updateCustomerTransaction);
  const deleteCustomerTransactionFromContext = (id) =>
    deleteItem('CustomerTransactions', id, deleteCustomerTransaction);

  const removeCustomerTransactionAndUpdateStockInContext = async (data) => {
    setLoading(true);
    try {
      const {
        transactionId,
        customerId,
        billNo,
        items = [],
        silverItems = [],
        refund = {},
        cancelledAt
      } = data;

      const { cash = 0, bank = 0, total = 0 } = refund;
      const currentTime = new Date().toISOString();
      const todayDate = getTodayISTDate();

      console.log("Cancelling transaction:", {
        transactionId,
        billNo,
        itemsCount: items.length,
        silverItemsCount: silverItems.length,
        refund
      });

      // ---------- STEP 1: FIND THE TRANSACTION ----------
      const transactionIndex = data.CustomerTransactions.findIndex(
        t => t.id === transactionId
      );

      if (transactionIndex === -1) {
        throw new Error(`Transaction with ID ${transactionId} not found`);
      }

      const transaction = { ...data.CustomerTransactions[transactionIndex] };
      const originalTransactionDetails = JSON.parse(JSON.stringify(transaction.transactionDetails));

      // Check if already cancelled
      if (transaction.transactionDetails?.type === 'cancelled') {
        throw new Error(`Transaction ${transactionId} is already cancelled`);
      }

      // ---------- STEP 2: UPDATE TRANSACTION DETAILS TO CANCELLED ----------
      const updatedTransaction = {
        ...transaction,
        transactionDetails: {
          ...transaction.transactionDetails,
          type: 'cancelled'
        },
        updated_at: currentTime
      };

      // ---------- STEP 3: PROCESS SILVER ITEMS ----------
      const silverUpdateResults = [];
      let updatedSilverStock = { ...data.SilverStock };
      let updatedSilverTransactions = [...(data.SilverTransactions || [])];

      if (silverItems && silverItems.length > 0) {
        console.log(`Processing ${silverItems.length} silver items from cancellation`);

        silverItems.forEach((silverGroup, groupIndex) => {
          const silverType = silverGroup.silverType;
          const totalWeight = parseFloat(silverGroup.totalWeight) || 0;

          if (totalWeight > 0) {
            const currentWeight = parseFloat(updatedSilverStock[silverType]) || 0;
            const newWeight = currentWeight + totalWeight; // Add back the weight

            // Update silver stock
            updatedSilverStock[silverType] = newWeight;
            updatedSilverStock.timestamp = currentTime;

            // Create silver transaction record
            const silverTransaction = {
              id: `silver-cancel-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              date: todayDate,
              silverType: silverType,
              netWeight: totalWeight,
              operation: "add",
              description: `Returned from customer: Bill ${billNo}`,
              timestamp: currentTime,
              billNo: billNo,
              customerId: customerId,
              transactionId: transactionId,
              reason: "Transaction cancellation - silver returned",
              isLocal: true,
              synced: false
            };

            // Add to silver transactions
            updatedSilverTransactions = [silverTransaction, ...updatedSilverTransactions];

            silverUpdateResults.push({
              silverType: silverType,
              success: true,
              operation: "add",
              weightChanged: totalWeight,
              previousWeight: currentWeight,
              newWeight: newWeight
            });

            console.log(`Silver stock reversed: ${silverType} +${totalWeight}g (${currentWeight}g → ${newWeight}g)`);
          }
        });
      }

      // ---------- STEP 4: FILTER OUT SILVER TRANSACTIONS RELATED TO THIS BILL ----------
      const filteredSilverTransactions = updatedSilverTransactions.filter(silverTrans => {
        // Keep transactions that are not related to this cancellation
        const isRelated = (
          silverTrans.billNo === billNo ||
          (silverTrans.description && silverTrans.description.includes(`Bill ${billNo}`)) ||
          (silverTrans.customerId && silverTrans.customerId === customerId) ||
          (silverTrans.transactionId && silverTrans.transactionId === transactionId)
        );

        return !isRelated;
      });

      // ---------- STEP 5: REVERSE CUSTOMER BALANCE UPDATE ----------
      let customerUpdated = false;
      let currentCustomerBalance = 0;
      let balanceChange = 0;
      let updatedCustomers = [...data.Customers];

      if (customerId) {
        const customerIndex = updatedCustomers.findIndex(
          c => c.id === customerId
        );

        if (customerIndex !== -1) {
          const customer = updatedCustomers[customerIndex];
          const oldBalance = parseFloat(customer.currentBalance) || 0;

          // Reverse the payment made during the original transaction
          const pendingAmount = parseFloat(transaction.summary?.pendingAmount) || 0;
          const paymentDone = parseFloat(transaction.paymentDetails?.totalPaymentDone) || 0;

          if (pendingAmount < 0) {
            // Customer paid extra, need to refund
            balanceChange = pendingAmount + paymentDone;
          } else if (pendingAmount > 0) {
            // Customer owes money, reduce their debt
            balanceChange = pendingAmount + paymentDone;
          }

          // Adjust for refund amount from API
          balanceChange -= total;

          currentCustomerBalance = parseFloat((oldBalance - balanceChange).toFixed(0));

          // Update customer balance
          updatedCustomers[customerIndex] = {
            ...customer,
            currentBalance: currentCustomerBalance,
            updated_at: currentTime
          };

          customerUpdated = true;
          console.log(`Customer ${customerId} balance updated: ${oldBalance} -> ${currentCustomerBalance} (change: ${balanceChange})`);
        } else {
          console.warn(`Customer with ID ${customerId} not found`);
        }
      }

      // ---------- STEP 6: UPDATE STOCK ITEMS ----------
      const stockUpdateResults = [];
      let updatedStock = [...data.Stock];

      if (items && items.length > 0) {
        items.forEach((item, itemIndex) => {
          const stockId = item.id;
          const huid = item.huid;
          const name = item.name;
          const originalSerialNo = item.originalSerialNo;
          const newSerialNo = item.newSerialNo;
          const updateData = item.updateData || {};
          const hasConflict = item.hasConflict || false;

          if (!stockId) {
            console.warn(`Item ${itemIndex + 1} has no stock ID. Skipping.`);
            stockUpdateResults.push({
              stockId: 'N/A',
              huid: huid,
              name: name,
              status: 'skipped',
              message: 'No stock ID provided'
            });
            return;
          }

          const stockIndex = updatedStock.findIndex(
            s => s.id === stockId
          );

          if (stockIndex === -1) {
            console.warn(`Stock item with ID ${stockId} not found. Skipping.`);
            stockUpdateResults.push({
              stockId: stockId,
              huid: huid,
              name: name,
              status: 'skipped',
              message: 'Stock item not found'
            });
            return;
          }

          // Verify HUID matches if provided
          if (huid) {
            const currentHuid = updatedStock[stockIndex].Huid || '';
            if (currentHuid !== huid) {
              console.warn(`HUID mismatch for stock ${stockId}. Expected: ${huid}, Found: ${currentHuid}. Proceeding anyway.`);
            }
          }

          // Check for serial number conflicts
          let finalSerialNo = originalSerialNo;
          if (hasConflict && newSerialNo) {
            finalSerialNo = newSerialNo;
            console.log(`Using new serial number ${newSerialNo} due to conflict for stock ${stockId}`);
          }

          // Update the stock item
          try {
            const stockUpdates = {
              ...updateData,
              Sold: updateData.sold || "No",
              SoldDescription: updateData.soldDescription || null,
              updated_at: currentTime
            };

            // Update serial number if changed
            if (finalSerialNo && finalSerialNo !== originalSerialNo) {
              stockUpdates.SerialNo = finalSerialNo;
            }

            updatedStock[stockIndex] = {
              ...updatedStock[stockIndex],
              ...stockUpdates
            };

            stockUpdateResults.push({
              stockId: stockId,
              huid: huid,
              name: name,
              originalSerialNo: originalSerialNo,
              newSerialNo: finalSerialNo,
              status: 'updated',
              message: hasConflict ? 'Updated with new serial number due to conflict' : 'Successfully updated'
            });

            console.log(`Stock item ${stockId} (${name}) marked as unsold`);

          } catch (error) {
            console.error(`Error updating stock item ${stockId}:`, error);
            stockUpdateResults.push({
              stockId: stockId,
              huid: huid,
              name: name,
              status: 'error',
              message: `Update failed: ${error.message}`
            });
          }
        });
      }

      // ---------- STEP 7: UPDATE ROZNAMA AS EXPENSE (FOR REFUND) ----------
      let updatedRoznama = [...data.Roznama];

      // Get customer name for roznama entry
      const customerName = customerUpdated
        ? updatedCustomers.find(c => c.id === customerId)?.name || "Unknown Customer"
        : "Unknown Customer";

      // Create expense transaction object for refund
      const expenseTransaction = {
        id: `expense-${transactionId}-${Date.now()}`,
        type: "expense",
        date: todayDate,
        timestamp: currentTime,
        description: `Transaction Cancelled - Refund: Bill ${billNo} - ${customerName}`,
        customerId: customerId,
        transactionId: transactionId,
        billNo: billNo,
        cash: parseFloat(cash) || 0,
        bank: parseFloat(bank) || 0,
        totalPaid: parseFloat(total) || 0,
        category: "Transaction Refund",
        notes: `Cancelled transaction ${transactionId}, refund issued`,
        isAutoGenerated: true,
        referenceId: transactionId
      };

      // Find roznama for TODAY'S date
      const roznamaIndex = updatedRoznama.findIndex(r => {
        try {
          const rDate = new Date(r.date).toISOString().split('T')[0];
          return rDate === todayDate;
        } catch (error) {
          return false;
        }
      });

      if (roznamaIndex >= 0) {
        // Update existing roznama for TODAY
        const existingRoznama = { ...updatedRoznama[roznamaIndex] };

        // Parse existing expense transactions
        const existingExpenseTransactions = Array.isArray(existingRoznama.expenseTransactions)
          ? existingRoznama.expenseTransactions
          : [];

        // Add expense transaction for refund
        existingRoznama.expenseTransactions = [...existingExpenseTransactions, expenseTransaction];

        // Recalculate totals
        existingRoznama.totalIncome = calculateTotal(existingRoznama.incomeTransactions || [], "income");
        existingRoznama.totalExpense = calculateTotal(existingRoznama.expenseTransactions || [], "expense");
        existingRoznama.netProfit = existingRoznama.totalIncome - existingRoznama.totalExpense;

        // Update closing balances (subtract refund amount)
        existingRoznama.closingBalanceCash = parseFloat(existingRoznama.closingBalanceCash || 0) - parseFloat(cash);
        existingRoznama.closingBalanceBank = parseFloat(existingRoznama.closingBalanceBank || 0) - parseFloat(bank);
        existingRoznama.updated_at = currentTime;

        updatedRoznama[roznamaIndex] = existingRoznama;

      } else {
        // Create new roznama entry for TODAY
        const newRoznamaId = `temp-${Date.now()}`;

        // Get previous day's closing balances (simplified - you might want to implement proper logic)
        const lastRoznama = updatedRoznama.length > 0
          ? updatedRoznama[updatedRoznama.length - 1]
          : null;

        const prevClosingCash = lastRoznama?.closingBalanceCash || 0;
        const prevClosingBank = lastRoznama?.closingBalanceBank || 0;

        const expenseCash = parseFloat(cash) || 0;
        const expenseBank = parseFloat(bank) || 0;
        const totalExpenseAmount = expenseCash + expenseBank;

        const newRoznama = {
          id: newRoznamaId,
          date: todayDate,
          openingBalanceCash: prevClosingCash,
          openingBalanceBank: prevClosingBank,
          closingBalanceCash: prevClosingCash - expenseCash,
          closingBalanceBank: prevClosingBank - expenseBank,
          incomeTransactions: [],
          expenseTransactions: [expenseTransaction],
          netProfit: -totalExpenseAmount,
          totalIncome: 0,
          totalExpense: totalExpenseAmount,
          created_at: currentTime,
          updated_at: currentTime
        };

        updatedRoznama.push(newRoznama);
      }

      // Sort roznama by date (most recent first)
      updatedRoznama.sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateB - dateA;
      });

      // ---------- STEP 8: UPDATE ALL DATA IN SINGLE STATE UPDATE ----------
      setData(prev => {
        // Update transaction in the array
        const updatedTransactions = [...prev.CustomerTransactions];
        updatedTransactions[transactionIndex] = updatedTransaction;

        return {
          ...prev,
          Customers: updatedCustomers,
          CustomerTransactions: updatedTransactions,
          Stock: updatedStock,
          SilverStock: updatedSilverStock,
          SilverTransactions: filteredSilverTransactions,
          Roznama: updatedRoznama
        };
      });

      console.log("Transaction cancelled successfully in frontend context");

      return {
        success: true,
        data: {
          message: "Transaction cancelled and reversed successfully",
          transactionId: transactionId,
          cancellationDetails: {
            originalType: originalTransactionDetails.type,
            newType: 'cancelled',
            billNo: billNo,
            cancelledAt: cancelledAt || currentTime
          },
          customerUpdate: {
            customerId: customerId,
            balanceUpdated: customerUpdated,
            newBalance: currentCustomerBalance,
            balanceChange: balanceChange
          },
          silverUpdates: {
            performed: silverItems.length > 0,
            count: silverItems.length,
            results: silverUpdateResults
          },
          stockUpdates: {
            performed: items.length > 0,
            count: items.length,
            results: stockUpdateResults,
            itemsWithConflicts: items.filter(item => item.hasConflict).length
          },
          refundDetails: {
            cash: cash,
            bank: bank,
            total: total
          },
          roznamaUpdate: {
            updated: true,
            transactionType: "expense"
          },
          timestamps: {
            cancelled_at: currentTime
          }
        }
      };

    } catch (error) {
      console.error("Error in removeCustomerTransactionAndUpdateStockInContext:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to cancel transaction"
      };
    } finally {
      setLoading(false);
    }
  };

  // Also add a separate function to handle the API call and update context
  const removeCustomerTransactionAndUpdateStock = async (data) => {
    setLoading(true);
    try {
      // First, update the backend
      const response = await removeCustomerTransactionAndUpdateStockApi(data);

      if (response?.success) {
        // If backend update successful, also update the context
        const contextResult = await removeCustomerTransactionAndUpdateStockInContext(data);

        if (contextResult.success) {
          return {
            success: true,
            data: contextResult.data,
            message: "Transaction cancelled successfully"
          };
        } else {
          throw new Error(contextResult.error || "Failed to update frontend context");
        }
      } else {
        throw new Error(response?.error || "Failed to cancel transaction on server");
      }
    } catch (error) {
      console.error("Error cancelling transaction:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to cancel transaction"
      };
    } finally {
      setLoading(false);
    }
  };

  // Stock
  const addStockToContext = (stockData) => {
    // Ensure SoldDescription field exists with proper default and is stringified if object
    let soldDescription = stockData.SoldDescription;

    if (soldDescription && typeof soldDescription === 'object') {
      soldDescription = JSON.stringify(soldDescription);
    }

    const stockWithDefaults = {
      ...stockData,
      Sold: stockData.Sold || "No",
      SoldDescription: soldDescription || null
    };
    return createItem('Stock', stockWithDefaults, addStock);
  };

  const updateStockInContext = (id, stockData) => {
    // If marking as sold, ensure SoldDescription is set
    if (stockData.Sold === "Yes" && !stockData.SoldDescription) {
      const stockWithDescription = {
        ...stockData,
        SoldDescription: stockData.SoldDescription || JSON.stringify({
          sold_date: new Date().toISOString().split('T')[0],
          sold_bill_no: "MANUAL",
          notes: "Manually marked as sold"
        })
      };
      return updateItem('Stock', id, stockWithDescription, updateStock);
    }
    // If SoldDescription is an object, stringify it
    if (stockData.SoldDescription && typeof stockData.SoldDescription === 'object') {
      const stockWithStringifiedDesc = {
        ...stockData,
        SoldDescription: JSON.stringify(stockData.SoldDescription)
      };
      return updateItem('Stock', id, stockWithStringifiedDesc, updateStock);
    }
    return updateItem('Stock', id, stockData, updateStock);
  };


  const deleteStockFromContext = (id) =>
    deleteItem('Stock', id, deleteStock);

  // Roznama

  // Roznama
  // const addRoznamaToContext = (roznamaData) =>
  //   createItem('Roznama', roznamaData, addRoznamaDay);

  // const updateRoznamaInContext = (id, roznamaData) =>
  //   updateItem('Roznama', id, roznamaData, updateRoznamaDay);

  const addRoznamaToContext = async (roznamaData) => {
    setLoading(true);
    try {
      console.log("Adding new roznama:", roznamaData);

      // Ensure all required fields are present with proper calculations
      const completeRoznamaData = {
        ...roznamaData,
        closingBalanceCash: roznamaData.closingBalanceCash !== undefined
          ? roznamaData.closingBalanceCash
          : (parseFloat(roznamaData.openingBalanceCash) || 0) +
          (calculateTotal(roznamaData.incomeTransactions || [], 'cash') || 0) -
          (calculateTotal(roznamaData.expenseTransactions || [], 'cash') || 0),
        closingBalanceBank: roznamaData.closingBalanceBank !== undefined
          ? roznamaData.closingBalanceBank
          : (parseFloat(roznamaData.openingBalanceBank) || 0) +
          (calculateTotal(roznamaData.incomeTransactions || [], 'bank') || 0) -
          (calculateTotal(roznamaData.expenseTransactions || [], 'bank') || 0),
        netProfit: roznamaData.netProfit !== undefined
          ? roznamaData.netProfit
          : (calculateTotal(roznamaData.incomeTransactions || []) || 0) -
          (calculateTotal(roznamaData.expenseTransactions || []) || 0),
        totalIncome: roznamaData.totalIncome !== undefined
          ? roznamaData.totalIncome
          : calculateTotal(roznamaData.incomeTransactions || []) || 0,
        totalExpense: roznamaData.totalExpense !== undefined
          ? roznamaData.totalExpense
          : calculateTotal(roznamaData.expenseTransactions || []) || 0,
      };

      console.log("Complete roznama data to send:", completeRoznamaData);

      const response = await addRoznamaDay(completeRoznamaData);
      console.log("Roznama add response:", response);

      if (response?.success) {
        const newRoznama = response.data;
        console.log("New roznama from API:", newRoznama);

        // Update the context with new roznama
        setData(prev => {
          const updatedArray = [...prev.Roznama, newRoznama];
          console.log(`Roznama added to context, new count: ${updatedArray.length}`);

          return {
            ...prev,
            Roznama: updatedArray
          };
        });

        return {
          success: true,
          data: newRoznama,
          message: "Roznama added successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to add roznama");
      }
    } catch (error) {
      console.error("Error adding roznama:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to add roznama"
      };
    } finally {
      setLoading(false);
    }
  };

  const findRoznamaByDate = useCallback((date) => {
    const roznamaArray = data.Roznama || [];
    return roznamaArray.find((r) => {
      try {
        const roznamaDate = new Date(r.date);
        const searchDate = new Date(date);
        return roznamaDate.toISOString().split('T')[0] === searchDate.toISOString().split('T')[0];
      } catch (error) {
        return false;
      }
    });
  }, [data.Roznama]);

  const addOrUpdateRoznamaInContext = async (roznamaData) => {
    setLoading(true);
    try {
      // Check if roznama for this date already exists
      const existingRoznama = data.Roznama.find(r => {
        try {
          const rDate = new Date(r.date).toISOString().split('T')[0];
          const newDate = new Date(roznamaData.date).toISOString().split('T')[0];
          return rDate === newDate;
        } catch (error) {
          return false;
        }
      });

      if (existingRoznama) {
        // Update existing roznama
        console.log(`Updating existing roznama for date: ${roznamaData.date}`);
        return await updateRoznamaInContext(existingRoznama.id, roznamaData);
      } else {
        // Add new roznama
        console.log(`Adding new roznama for date: ${roznamaData.date}`);
        return await addRoznamaToContext(roznamaData);
      }
    } catch (error) {
      console.error("Error in addOrUpdateRoznama:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to save roznama"
      };
    } finally {
      setLoading(false);
    }
  };

  const updateRoznamaInContext = async (id, roznamaData) => {
    setLoading(true);
    try {
      console.log(`Updating Roznama ID ${id}:`, roznamaData);

      // Ensure all required fields are present
      const completeRoznamaData = {
        ...roznamaData,
        // Calculate if not provided
        closingBalanceCash: roznamaData.closingBalanceCash !== undefined
          ? roznamaData.closingBalanceCash
          : (roznamaData.openingBalanceCash || 0) +
          (calculateTotal(roznamaData.incomeTransactions || [], 'cash') || 0) -
          (calculateTotal(roznamaData.expenseTransactions || [], 'cash') || 0),
        closingBalanceBank: roznamaData.closingBalanceBank !== undefined
          ? roznamaData.closingBalanceBank
          : (roznamaData.openingBalanceBank || 0) +
          (calculateTotal(roznamaData.incomeTransactions || [], 'bank') || 0) -
          (calculateTotal(roznamaData.expenseTransactions || [], 'bank') || 0),
        netProfit: roznamaData.netProfit !== undefined
          ? roznamaData.netProfit
          : (calculateTotal(roznamaData.incomeTransactions || []) || 0) -
          (calculateTotal(roznamaData.expenseTransactions || []) || 0),
        totalIncome: roznamaData.totalIncome !== undefined
          ? roznamaData.totalIncome
          : calculateTotal(roznamaData.incomeTransactions || []) || 0,
        totalExpense: roznamaData.totalExpense !== undefined
          ? roznamaData.totalExpense
          : calculateTotal(roznamaData.expenseTransactions || []) || 0,
      };
      console.log(completeRoznamaData)
      const response = await updateRoznamaDay(id, completeRoznamaData);
      console.log("Roznama update response:", response);

      if (response?.success) {
        const updatedRoznama = response.data;
        setData(prev => {
          const updatedArray = prev.Roznama.map(item =>
            item.id === id ? { ...item, ...updatedRoznama } : item
          );

          return {
            ...prev,
            Roznama: updatedArray
          };
        });

        return {
          success: true,
          data: updatedRoznama,
          message: "Roznama updated successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to update roznama");
      }
    } catch (error) {
      console.error("Error updating roznama:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to update roznama"
      };
    } finally {
      setLoading(false);
    }
  };

  const deleteRoznamaFromContext = (id) =>
    deleteItem('Roznama', id, deleteRoznamaDay);

  // Suppliers
  const addSupplierToContext = (supplierData) =>
    createItem('Suppliers', supplierData, addSupplier);

  const updateSupplierInContext = (id, supplierData) =>
    updateItem('Suppliers', id, supplierData, updateSupplier);

  const deleteSupplierFromContext = (id) =>
    deleteItem('Suppliers', id, deleteSupplier);

  // Supplier Transactions
  const addSupplierTransactionToContext = async (transactionData) => {
    setLoading(true);
    try {
      console.log("Adding supplier transaction:", transactionData);
      const response = await addSupplierTransaction(transactionData);
      console.log("Supplier transaction add response:", response);

      if (response?.success) {
        const newTransaction = response.data;
        const supplierId = transactionData.supplier;
        const summary = transactionData.summary || {};

        // Get values from summary
        const grandTotal = parseFloat(summary.grandTotal) || 0;
        const fineBalance = parseFloat(summary.fineBalance) || 0;
        const finePayment = parseFloat(summary.finePayment) || 0;

        // Get payments from transaction
        const payments = transactionData.payments || {};
        const cashPayment = parseFloat(payments.cash) || 0;
        const bankPayment = parseFloat(payments.bank) || 0;

        // Get balance type from transaction
        const balanceType = transactionData.balanceType || '';

        console.log("Calculating supplier balance updates:", {
          supplierId,
          balanceType,
          grandTotal,
          fineBalance,
          finePayment,
          cashPayment,
          bankPayment
        });

        // Update all related data in a single state update
        setData(prev => {
          const currentTime = new Date().toISOString();

          // 1. Add the new transaction
          const updatedTransactions = [...prev.SupplierTransactions, newTransaction];

          // 2. Update supplier's balances
          const updatedSuppliers = prev.Suppliers.map(supplier => {
            if (supplier.id === supplierId) {
              // Get current balances
              let newBalanceCash = parseFloat(supplier.balanceValueCash) || 0;
              let newBalanceBank = parseFloat(supplier.balanceValueBank) || 0;
              let newBalanceFine = parseFloat(supplier.balanceFine) || 0;

              console.log("Supplier current balances:", {
                cash: newBalanceCash,
                bank: newBalanceBank,
                fine: newBalanceFine
              });

              // BACKEND LOGIC MIRRORED EXACTLY:
              // 1. Deduct cash payment from BalanceValueCash
              if (cashPayment > 0) {
                newBalanceCash -= cashPayment;
                console.log(`Deducted cash payment ${cashPayment} from BalanceValueCash. New: ${newBalanceCash}`);
              }

              // 2. Deduct bank payment from BalanceValueBank
              if (bankPayment > 0) {
                newBalanceBank -= bankPayment;
                console.log(`Deducted bank payment ${bankPayment} from BalanceValueBank. New: ${newBalanceBank}`);
              }

              // 3. Deduct fine payment from BalanceFine
              if (finePayment > 0) {
                newBalanceFine -= finePayment;
                console.log(`Deducted fine payment ${finePayment} from BalanceFine. New: ${newBalanceFine}`);
              }

              // 4. Add grand total to appropriate balance based on balanceType
              if (grandTotal > 0) {
                if (balanceType === 'cash') {
                  newBalanceCash += grandTotal;
                  console.log(`Added grand total ${grandTotal} to BalanceValueCash. New: ${newBalanceCash}`);
                } else if (balanceType === 'bank') {
                  newBalanceBank += grandTotal;
                  console.log(`Added grand total ${grandTotal} to BalanceValueBank. New: ${newBalanceBank}`);
                } else {
                  console.warn(`No balanceType specified. Grand total ${grandTotal} not added to any balance.`);
                }
              }

              // 5. Add fine balance to BalanceFine
              if (fineBalance > 0) {
                newBalanceFine += fineBalance;
                console.log(`Added fine balance ${fineBalance} to BalanceFine. New: ${newBalanceFine}`);
              }

              console.log("Supplier updated balances:", {
                cash: newBalanceCash,
                bank: newBalanceBank,
                fine: newBalanceFine
              });

              return {
                ...supplier,
                balanceValueCash: newBalanceCash,
                balanceValueBank: newBalanceBank,
                balanceFine: newBalanceFine,
                updated_at: currentTime
              };
            }
            return supplier;
          });

          // 3. Update Roznama with expense transaction
          let updatedRoznama = [...prev.Roznama];
          // let newDate = new Date(`${transactionData.date}T00:00:00+05:30`).toISOString();
          const todayDate = getTodayISTDate();
          const transactionId = newTransaction.id;
          console.log(transactionData)
          // Create roznama expense transaction (matching backend structure)
          const roznamaExpenseTransaction = {
            id: transactionId,
            date: todayDate,
            type: "expense",
            billNumber: `SUPP-${transactionId.substring(0, 8)}`,
            customerSupplier: prev.Suppliers.find(s => s.id === supplierId)?.name || "Unknown Supplier",
            description: transactionData.items
              ?.map((item) => item.itemName || item.name || "Item")
              .join(", ") || "Supplier Purchase",
            amount: grandTotal || 0,
            totalPaid: summary.paymentDone || 0,
            fine: fineBalance || 0,
            bank: bankPayment || 0,
            cash: cashPayment || 0,
            finePaid: finePayment || 0,
            totalPending: parseFloat(summary.pendingAmount || 0).toFixed(0),
            paymentMode: bankPayment > 0 && cashPayment === 0 ? "bank" :
              bankPayment === 0 && cashPayment > 0 ? "cash" : "split",
            remark: "Auto-generated from supplier transaction",
            transactionType: "supplier",
            isAutoGenerated: true,
            referenceId: transactionId,
            created_at: currentTime
          };

          // Find roznama for this date
          const roznamaIndex = updatedRoznama.findIndex(r => {
            try {
              const rDate = new Date(r.date).toISOString().split('T')[0];
              console.log(rDate)
              console.log(r.date)
              console.log(todayDate)
              return rDate === todayDate;
            } catch (error) {
              return false;
            }
          });

          if (roznamaIndex >= 0) {
            // Update existing roznama
            const existingRoznama = { ...updatedRoznama[roznamaIndex] };

            // Parse existing transactions
            const existingExpenseTransactions = Array.isArray(existingRoznama.expenseTransactions)
              ? existingRoznama.expenseTransactions
              : [];

            // Add new expense transaction
            existingRoznama.expenseTransactions = [...existingExpenseTransactions, roznamaExpenseTransaction];

            // Recalculate totals (mirroring backend logic)
            const transactionAmount = parseFloat(roznamaExpenseTransaction.totalPaid) || 0;

            // Update total expense
            existingRoznama.totalExpense = (parseFloat(existingRoznama.totalExpense) || 0) + transactionAmount;

            // Recalculate net profit
            existingRoznama.netProfit = (parseFloat(existingRoznama.totalIncome) || 0) - (parseFloat(existingRoznama.totalExpense) || 0);

            // Update closing balances
            const cashPaid = parseFloat(roznamaExpenseTransaction.cash) || 0;
            const bankPaid = parseFloat(roznamaExpenseTransaction.bank) || 0;

            existingRoznama.closingBalanceCash = (parseFloat(existingRoznama.closingBalanceCash) || 0) - cashPaid;
            existingRoznama.closingBalanceBank = (parseFloat(existingRoznama.closingBalanceBank) || 0) - bankPaid;
            existingRoznama.updated_at = currentTime;

            updatedRoznama[roznamaIndex] = existingRoznama;
          } else {
            // Create new roznama entry (simplified - backend would calculate opening balances)
            console.log(updatedRoznama)
            console.log(updatedRoznama[updatedRoznama.length - 1])
            let prevRoznama = updatedRoznama[updatedRoznama.length - 1];
            // let todaysRoznama = updatedRoznama[updatedRoznama.length - 1];
            const newRoznama = {
              id: `temp-${Date.now()}`,
              date: todayDate,
              openingBalanceCash: prevRoznama?.closingBalanceCash,
              openingBalanceBank: prevRoznama?.closingBalanceBank,
              closingBalanceCash: Number(prevRoznama?.closingBalanceCash) - cashPayment, // Simplified
              closingBalanceBank: Number(prevRoznama?.closingBalanceBank) - bankPayment, // Simplified
              incomeTransactions: [],
              expenseTransactions: [roznamaExpenseTransaction],
              netProfit: 0 - cashPayment + bankPayment,
              totalIncome: 0,
              totalExpense: cashPayment + bankPayment,
              created_at: currentTime,
              updated_at: currentTime
            };
            updatedRoznama.push(newRoznama);
          }

          console.log("State updated:", {
            newTransactionId: newTransaction.id,
            supplierUpdated: true,
            supplierId,
            roznamaUpdated: true
          });

          return {
            ...prev,
            Suppliers: updatedSuppliers,
            SupplierTransactions: updatedTransactions,
            Roznama: updatedRoznama
          };
        });

        return {
          success: true,
          data: newTransaction,
          message: "Supplier transaction added and balances updated successfully"
        };
      } else {
        throw new Error(response?.error || "Failed to add supplier transaction");
      }
    } catch (error) {
      console.error("Error adding supplier transaction:", error);
      return {
        success: false,
        error: error.message,
        message: "Failed to add supplier transaction"
      };
    } finally {
      setLoading(false);
    }
  };

  const updateSupplierTransactionInContext = (id, transactionData) =>
    updateItem('SupplierTransactions', id, transactionData, updateSupplierTransaction);

  const deleteSupplierTransactionFromContext = (id) =>
    deleteItem('SupplierTransactions', id, deleteSupplierTransaction);

  // Girvi
  const addGirviToContext = (girviData) =>
    createItem('Girvi', girviData, addGirvi);

  const updateGirviInContext = (id, girviData) =>
    updateItem('Girvi', id, girviData, updateGirvi);

  const deleteGirviFromContext = (id) =>
    deleteItem('Girvi', id, deleteGirvi);

  // Girvi Payments
  const addGirviPaymentToContext = (paymentData) =>
    createItem('GirviPayments', paymentData, addGirviPayment);

  const updateGirviPaymentInContext = (id, paymentData) =>
    updateItem('GirviPayments', id, paymentData, updateGirviPayment);

  const deleteGirviPaymentFromContext = (id) =>
    deleteItem('GirviPayments', id, deleteGirviPayment);

  // Batch operations
  // const addMultipleItems = async (dataType, itemsData, serviceFunction) => {
  //   setLoading(true);
  //   try {
  //     const results = [];
  //     for (const itemData of itemsData) {
  //       const result = await createItem(dataType, itemData, serviceFunction);
  //       results.push(result);
  //     }
  //     return results;
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  // Search function across all data types
  const searchData = useCallback((searchTerm) => {
    const results = {};
    const searchLower = searchTerm.toLowerCase();

    Object.keys(data).forEach(dataType => {
      results[dataType] = data[dataType].filter(item => {
        // Search in common fields
        return Object.values(item).some(value =>
          String(value).toLowerCase().includes(searchLower)
        );
      });
    });

    return results;
  }, [data]);
  console.log("DataContext render with data:", data);
  const value = {
    // Loading state
    loading,

    recordAdvancePayment,
    fetchCustomerAdvanceBalance,
    applyAdvanceToBill,
    getAllAdvancePaymentsList,
    getAdvanceSummary,
    getCustomerAdvanceHistory,
    addAdvancePayment,
    withdrawAdvance,

    // Authentication state
    currentUser,
    isAuthenticated,

    // New payment management functions
    recordPartialPayment,
    getPaymentHistory,
    getPendingPayments,
    getCustomerBalanceSummary,

    // Direct access to all data (uppercase to match your API)
    customers: data.Customers,
    customerTransactions: data.CustomerTransactions,
    stock: data.Stock,
    suppliers: data.Suppliers,
    supplierTransactions: data.SupplierTransactions,
    girvi: data.Girvi,
    girviPayments: data.GirviPayments,
    roznama: data.Roznama,
    companyDetails: data.CompanyDetails,
    silverStock: data.SilverStock,
    silverTransactions: data.SilverTransactions,
    schemeCard: data.SchemeCard,

    // Generic functions
    updateData,
    getDataByType,
    addDataType,
    searchData,

    // Refresh
    refresh: loadInitialData,

    // Customer functions
    addCustomer: addCustomerToContext,
    updateCustomer: updateCustomerInContext,
    deleteCustomer: deleteCustomerFromContext,

    // Scheme Card functions
    addSchemeCard: addSchemeCardToContext,
    updateSchemeCard: updateSchemeCardInContext,
    deleteSchemeCard: deleteSchemeCardFromContext,

    // Transaction functions
    addCustomerTransaction: addCustomerTransactionToContext,
    updateCustomerTransaction: updateCustomerTransactionInContext,
    deleteCustomerTransaction: deleteCustomerTransactionFromContext,
    addCustomerTransactionAndUpdateStock: addCustomerTransactionAndUpdateStock,
    removeCustomerTransactionAndUpdateStock: removeCustomerTransactionAndUpdateStock,

    // Stock functions
    addStock: addStockToContext,
    updateStock: updateStockInContext,
    deleteStock: deleteStockFromContext,

    // Stock helper functions
    formatSoldDescription,
    markStockAsSold,
    getSoldItems,
    getSoldDescription,

    // Supplier functions
    addSupplier: addSupplierToContext,
    updateSupplier: updateSupplierInContext,
    deleteSupplier: deleteSupplierFromContext,

    // Supplier Transaction functions
    addSupplierTransaction: addSupplierTransactionToContext,
    updateSupplierTransaction: updateSupplierTransactionInContext,
    deleteSupplierTransaction: deleteSupplierTransactionFromContext,

    // Girvi functions
    addGirvi: addGirviToContext,
    updateGirvi: updateGirviInContext,
    deleteGirvi: deleteGirviFromContext,

    // Girvi Payment functions
    addGirviPayment: addGirviPaymentToContext,
    updateGirviPayment: updateGirviPaymentInContext,
    deleteGirviPayment: deleteGirviPaymentFromContext,

    // Roznama functions
    // loadRoznamaForDate,
    // saveRoznama: saveRoznamaToContext,
    closeRoznamaDay: closeRoznamaDayInContext,
    addRoznama: addRoznamaToContext,
    updateRoznama: updateRoznamaInContext,
    deleteRoznama: deleteRoznamaFromContext,
    findRoznamaByDate,
    saveRoznama: addOrUpdateRoznamaInContext,

    // ========= COMPANY DETAILS FUNCTIONS =========
    getCompanyDetails: getCompanyDetailsFromContext,
    updateCompanyDetails: updateCompanyDetailsInContext,
    addCompanyDetails: addCompanyDetailsToContext,

    // ========= SILVER STOCK FUNCTIONS =========
    getSilverStock: getSilverStockFromContext,
    updateSilverStock: updateSilverStockInContext,
    bulkUpdateSilverStock: bulkUpdateSilverStockInContext,
    refreshSilverStock,
    initializeSilverStock: initializeSilverStockInContext,
    
    // ========= BULK STOCK FUNCTIONS =========
    bulkUpdateStock: bulkUpdateStockInContext,
    bulkDeleteStock: bulkDeleteStockInContext,

    // ========= SILVER TRANSACTIONS FUNCTIONS =========
    getSilverTransactions: getSilverTransactionsFromContext,
    refreshSilverTransactions,

    // ========= AUTHENTICATION FUNCTIONS =========
    login: loginInContext,
    logout: logoutInContext,
    sendPasswordResetOtp: sendPasswordResetOtpInContext,
    verifyPasswordResetOtp: verifyPasswordResetOtpInContext,
    resetPassword: resetPasswordInContext,
    updatePassword: updatePasswordInContext,

    // Statistics
    getStats: () => ({
      customers: data.Customers.length,
      transactions: data.CustomerTransactions.length,
      stock: data.Stock.length,
      suppliers: data.Suppliers.length,
      supplierTransactions: data.SupplierTransactions.length,
      girvi: data.Girvi.length,
      girviPayments: data.GirviPayments.length,
      companyDetails: data.CompanyDetails.length,
      silverTransactions: data.SilverTransactions.length
    })
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};